import { CompleteServiceData } from './EnhancedServiceData';

// Re-export the interface for convenience
export type { CompleteServiceData } from './EnhancedServiceData';

// Cloud Cost Optimization & FinOps Consulting Services
const cloudCostOptimizationService: CompleteServiceData = {
  hero: {
    icon: '/assets/finops.svg',
    category: 'FinOps',
    title: 'Cloud Cost Optimization & FinOps Consulting Services',
    subtitle: 'Maximize Your Cloud ROI — Without Sacrificing Performance',
    description: 'Reduce your AWS, GCP, Azure, and Kubernetes costs with our expert-led FinOps strategies.',
    highlights: [
      '30–60% Cost Reduction',
      'Platform Agnostic',
      'Compliance-Safe',
      'Real-time monitoring & governance'
    ],
    ctaButtons: {
      primary: {
        text: 'Schedule a Free Audit',
        link: '/contact'
      },
      secondary: {
        text: 'Learn More',
        link: '/case-studies'
      }
    },
    leftImage: '/assets/services/finops-left-image.png',
    rightImage: '/assets/services/finops-right-image.png'   
  },
  content: {
    sections: [
      {
        title: 'Core FinOps Solutions',
        type: 'info-cards',
        info_cards_content: [
          {
            id: 'card1',
            number: '01',
            title: 'Control Cloud Costs Without Sacrificing Performance',
            description: 'Identify and eliminate idle compute, overprovisioned storage, and non-critical workloads. Implement auto-scaling, rightsizing, and spot/preemptible usage to reduce cloud spend.',
            features: [
              'Auto-scaling and rightsizing strategies',
              'Spot/preemptible instance optimization',
              'Resource utilization monitoring'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Control-Cloud-Costs.png'
          },
          {
            id: 'card2',
            number: '02',
            title: 'Usage Analytics & Visibility',
            description: 'Build dashboards using Prometheus, Grafana, or native billing APIs to map cost to environments, services, and teams. Enable real-time visibility across multi-cloud usage.',
            features: [
              'Real-time cost visibility',
              'Multi-cloud usage tracking',
              'Team-based cost attribution'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Usage-Analytics.png'
          },
          {
            id: 'card3',
            number: '03',
            title: 'Architecture-Aware Cost Efficiency',
            description: 'Redesign infrastructure for cost awareness: shift from monoliths to autoscaled microservices, optimize networking and storage classes based on traffic and data patterns.',
            features: [
              'Network optimization',
              'Storage class optimization',
              'Traffic pattern analysis'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Architecture-Aware.png'
          }
        ]
      },
      {
        title: 'Advanced Governance & Controls',
        type: 'animated-info-cards',
        animated_info_cards_content: [
          {
            id: 'card1',
            number: '01',
            title: 'Team & Access-Based Governance',
            description: 'Set up tagging policies, IAM access boundaries, and service quotas to attribute cost by team and prevent shadow IT. Use automation to enforce policy compliance.',
            features: [
              'Automated policy compliance',
              'Shadow IT prevention',
              'Cost ownership assignment'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Team&Access-Based.png'
          },
          {
            id: 'card2',
            number: '02',
            title: 'Intelligent Budget Controls',
            description: 'Define team-wise budgets and integrate alerting via Slack, Opsgenie, or PagerDuty when thresholds are breached. Automate scaling decisions based on forecasted load.',
            features: [
              'Threshold breach alerts',
              'Automated scaling decisions',
              'Forecasted load management'
            ],
            bgColor: 'white',
            image: '/assets/allServices/intelligent-Budget.png'
          },
          {
            id: 'card3',
            number: '03',
            title: 'Kubernetes Cost Breakdown',
            description: 'Break down Kubernetes costs by namespace, pod, or workload using Kubecost/OpenCost. Implement node pool segmentation, spot instance pools, and right-sizing policies for containerized workloads.',
            features: [
              'Node pool segmentation',
              'Spot instance pools',
              'Container rightsizing policies'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Kubernetes-Cost.png'
          }
        ]
      },
      {
        title: 'What Do We Offer in Cloud FinOps Consulting?',
        background:'blue',
        type: 'benefits-card',
        benefits_card_content: [
          {
            image: "FinOpsStrategyAdvisory.png",
            title: 'FinOps Strategy Advisory',
            description: 'We help you align your cloud investment with your growth strategy — not just reduce your bill.',
            features: [
              'Cost-first mindset establishment',
              'Cross-functional alignment',
              'Measurable business outcomes'
            ]
          },
          {
            image: "Intelligent-Cost-Monitoring.png",
            title: 'Intelligent Cost Monitoring & Automation',
            description: 'Manual cost tracking is slow, reactive, and error-prone. Our team implements intelligent automation.',
            features: [
              'Real-time anomaly detection',
              'Predictive cost thresholds',
              'Automated remediation actions'
            ]
          },
          {
            image: "CloudGovernance.png",
            title: 'Cloud Governance That Balances Cost, Security, and Control',
            description: 'Bring structure, compliance, and transparency across your cloud environments.',
            features: [
              'Policy-as-code practices',
              'Automated compliance enforcement',
              'Full cost attribution'
            ]
          },
          {
            image: "MigrationCostAssessment.png",
            title: 'Migration Cost Assessment',
            description: 'Get a clear financial and operational view before making migration decisions.',
            features: [
              'Total cost of ownership analysis',
              'Hidden cost driver identification',
              'ROI protection strategies'
            ]
          }
        ]
      },
      {
        title: 'Why Partner with Us for FinOps Consulting?',
        background:'white',
        type: 'grid-cards',
        grid_cards_content: [
          {
            image: "DeepEngineeringExpertise.png",
            title: 'Deep Engineering Expertise',
            description: 'Seasoned DevOps and SRE professionals with architecture-level thinking.',
            features: [
              'Multi-cloud platform expertise',
              'Container orchestration mastery',
              'Infrastructure optimization'
            ]
          },
          {
            image: "Serverless&Event-Driven.png",
            title: 'Serverless & Event-Driven Architecture Tuning',
            description: 'Optimize Lambda, Cloud Functions, or Step Functions for cost and performance.',
            features: [
              'Concurrency optimization',
              'Cold-start reduction',
              'Memory sizing tuning'
            ]
          },
          {
            image: "DatabaseCostOptimization.png",
            title: 'Database Cost Optimization',
            description: 'Analyze database workloads across RDS, Aurora, CloudSQL, MongoDB, and Elasticsearch.',
            features: [
              'Unused replica elimination',
              'IOPS waste reduction',
              'Instance class optimization'
            ]
          }
        ]
      },
      {
        title: 'Supported Tools & Technologies',
        type: 'tools',
        tools: [
          {
            subheading: "Cloud Platforms",
            text: "Our team specializes in deploying and managing applications across major cloud platforms. We help organizations leverage the full potential of cloud infrastructure for maximum scalability, reliability, and cost-efficiency.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization",
            ],
            image: "cloud-platforms.png"
          },
          {
            subheading: "Cost Monitoring",
            text: "Take control of your cloud spending with our comprehensive cost monitoring solutions. We provide real-time visibility into resource usage and spending patterns to help optimize your cloud investments.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization",
            ],
            image: "cost-monitoring.png"
          },
          {
            subheading: "Automation",
            text: "Streamline your operations with intelligent automation solutions. Our automation expertise helps eliminate repetitive tasks, reduce human error, and accelerate development and deployment processes.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization",
            ],
            image: "automation.png"
          },
          {
            subheading: "Alerting & Notification",
            text: "Stay ahead of potential issues with our robust alerting and notification systems. We implement comprehensive monitoring to ensure you're immediately informed of any critical events requiring attention.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization",
            ],
            image: "alerting-notification.png"
          }
        ]
      },
      {
        title: 'What Do We Offer in Cloud FinOps Consulting?',
        type:'rich-text',
        rich_text_content:[
          {
            percent : 40,
            barColor: "#10B981",
            topic: "Average Cost Reduction"
          },
          {
            percent : 95,
            barColor: "#3B82F6",
            topic: "Cost Visibility Increase"
          },
          {
            percent : 60,
            barColor: "#8B5CF6",
            topic: "Resource Efficiency Gain"
          }
        ]
      },
      {
        title: 'What You Get',
        background:'blue',
        type: 'features',
        features_content:[
          {
            text: "Real-time cost visibility across multi-cloud environments"
          },
          {
           text: "Team-level cost ownership and attribution"
          },
          {
            text: "Automated governance and policy enforcement"
          },
          {
            text: "Developer-integrated cost awareness tools"
          }
      ]
      }
    ]
  },
  industryUseCases: {
    title: 'Industries & Use Cases We Specialize In',
    subtitle: 'We bring domain-aware cost optimization strategies tailored to your cloud workloads, business models, and team structures.',
    useCases: [
      {
        icon: '🏢',
        title: 'SaaS Platforms',
        description: 'Optimize per-tenant cost in multi-tenant systems, implement per-service cost breakdown, and enable FinOps reporting for engineering and finance.',
        features: [
          'Multi-tenant cost optimization',
          'Per-service cost breakdown',
          'Engineering and finance alignment'
        ]
      },
      {
        icon: '🧬',
        title: 'ML & AI Workloads',
        description: 'Reduce GPU idle time, auto-scale training workloads, and optimize storage tiers for model checkpoints, datasets, and logs.',
        features: [
          'GPU idle time reduction',
          'Training workload auto-scaling',
          'Model storage optimization'
        ]
      },
      {
        icon: '🛒',
        title: 'E-commerce & Retail',
        description: 'Implement caching strategies, edge optimization, and auto-scaling APIs for sales peaks (e.g., during promotions), while minimizing infrastructure bloat off-season.',
        features: [
          'Peak traffic optimization',
          'Seasonal scaling strategies',
          'Edge caching implementation'
        ]
      },
      {
        icon: '🩺',
        title: 'HealthTech & Regulated Industries',
        description: 'Align with HIPAA/SOC2 budgets and enforce strict cost visibility, network isolation, and access control in cloud-native stacks.',
        features: [
          'HIPAA/SOC2 compliance',
          'Network isolation',
          'Audit-ready cost controls'
        ]
      },
      {
        icon: '🎮',
        title: 'High-Traffic Event Systems',
        description: 'Implement short-lived infrastructure patterns, real-time autoscaling, and anomaly-based alerting to manage burst workloads under budget.',
        features: [
          'Burst workload management',
          'Real-time autoscaling',
          'Anomaly-based alerting'
        ]
      },
      {
        icon: '🧪',
        title: 'Dev/Test Environments',
        description: 'Automate spin-up/spin-down of sandbox environments, enforce TTL policies, and isolate billing across teams and projects.',
        features: [
          'Environment automation',
          'TTL policy enforcement',
          'Team-based billing isolation'
        ]
      }
    ]
  },
  faq: {
    title: 'Frequently Asked Questions',
    faqs: [
      {
        question: 'How much can we realistically save on cloud costs?',
        answer: 'Most of our clients see 30-60% cost reduction within the first 6 months. The exact savings depend on your current infrastructure efficiency, workload patterns, and willingness to adopt cost-optimized architectures.'
      },
      {
        question: 'Will cost optimization impact our application performance?',
        answer: 'No. Our approach focuses on eliminating waste and inefficiencies while maintaining or improving performance. We use techniques like rightsizing, auto-scaling, and intelligent resource allocation that actually enhance performance characteristics.'
      },
      {
        question: 'How long does it take to implement FinOps strategies?',
        answer: 'Initial quick wins can be achieved within 2-4 weeks. A comprehensive FinOps implementation typically takes 3-6 months, depending on your infrastructure complexity and organizational readiness.'
      },
      {
        question: 'Do you support multi-cloud environments?',
        answer: 'Yes, we specialize in multi-cloud cost optimization across AWS, GCP, Azure, and hybrid environments. Our platform-agnostic approach ensures consistent cost management regardless of your cloud provider mix.'
      }
    ]
  }
};

const kubernetesConsultingService: CompleteServiceData = {
  hero: {
    icon: '/assets/services/kubernetes-consulting-service.svg',
    category: 'Infrastructure',
    title: 'Kubernetes Consulting Services',
    subtitle: 'Enterprise Kubernetes Architecture & Implementation',
    description: 'Leverage our deep expertise in Kubernetes architecture, automation, and security for robust container platforms that drive agility, scalability, and resilience.',
    highlights: [
      'Proven Enterprise Experience',
      'Automation-First Philosophy',
      'Security & Compliance Built In',
      'Cloud-Agnostic Expertise'
    ],
    leftImage: '/assets/services/kubernetes-left-image.png',
    rightImage: '/assets/services/kubernetes-right-image.png'
  },
  content: {
    sections: [
      {
        title: 'Why ReventLabs for Kubernetes Consulting?',
        type: 'info-cards',
        info_cards_content: [
          {
            id: 'card1',
            number: '01',
            title: 'Proven Enterprise Experience',
            description: 'We\'ve architected and managed Kubernetes environments for high-scale, 3000+ microservice landscapes, handling 10 million+ API hits/month.',
            features: [
              'High-scale deployments',
              '3000+ microservices managed',
              'Real-world demand handling'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Proven-Enterprise.png'
          },
          {
            id: 'card2',
            number: '02',
            title: 'Automation-First Philosophy',
            description: 'ReventLabs delivers prebuilt Helm charts, Terraform modules, and GitOps pipelines that eliminate manual toil.',
            features: [
              'Prebuilt Helm charts',
              'Terraform modules',
              'GitOps pipelines'
            ],
             bgColor: 'white',
            image: '/assets/allServices/Automation-First.png'
          },
          {
            id : 'card3',
            number: '02',
            title: 'Security & Compliance Built In',
            description: 'From CIS-hardened node images to automated Admission Controller policies, we embed security controls at every layer.',
            features: [
              'CIS-hardened node images',
              'Automated policies',
              'SOC 2 and PCI DSS guidance'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Security.png'
          },
          {
            id: 'card4',
            number: '04',
            title: 'Cloud-Agnostic Expertise (BYOC Enabled)',
            description: 'Whether you prefer AWS EKS, GCP GKE, Azure AKS, or on-prem clusters, our BYOC approach lets you retain account ownership.',
            features: [
              'Multi-cloud support',
              'Account ownership retention',
              'Consistent tooling across providers'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Cloud-Agnostic.png'
          }
        ]
      },
      {
        title: 'Why Partner with Us for Kubernetes Consulting?',
        type: 'animated-info-cards',
        animated_info_cards_content: [
          {
            id: 'card1',
            number: '01',
            title: 'Accelerated Time-to-Value',
            description: 'Deploy production-grade clusters quickly using prebuilt modules, GitOps pipelines, and runbooks.',
            features: [
              'Prebuilt modules for rapid setup',
              'GitOps pipelines integration',
              'Well-defined runbooks'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Time-to-Value.png'
          },
          {
            id: 'card2',
            number: '02',
            title: 'Resilient, Production-Grade Architecture',
            description: 'Ensure SLA adherence through high availability and automated recovery mechanisms.',
            features: [
              'HA control planes',
              'Zero-trust networking',
              'Automated recovery workflows'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Resilient.png'
          },
          {
            id: 'card3',
            number: '03',
            title: 'Cost-Efficient Operations',
            description: 'Optimize cloud costs with intelligent resource management strategies.',
            features: [
              'Rightsizing workloads',
              'Spot instance usage',
              'Continuous drift detection'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Cost-Efficient.png'
          },
          {
            id: 'card4',
            number: '04',
            title: 'Built-In Security & Compliance',
            description: 'Security and compliance are embedded into the lifecycle with policy-driven controls.',
            features: [
              'CIS-hardening practices',
              'Policy-as-code checks',
              'Automated audit logging'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Security.png'
          },
          {
            id: 'card5',
            number: '05',
            title: 'Future-Proof Scale',
            description: 'Leverage modular infrastructure and GitOps to scale across any environment.',
            features: [
              'Modular, declarative infrastructure',
              'GitOps-driven scaling',
              'Support for public, hybrid, and edge cloud'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Future-Proof.png'
          }
        ]
      },    
      {
        title: 'Core Kubernetes Consulting Offerings',
        background:'blue',
        type: 'benefits-card',
        benefits_card_content: [
          {
            image: "Cluster-Assessment.png",
            title: 'Cluster Assessment & Proof of Concept',
            description: 'Deep dive into your existing compute resources, networking, and workload topology with prioritized action plans and PoC implementation.'
          },
          {
            image: "ArchitectureDesign.png",
            title: 'Architecture Design & Deployment',
            description: 'High-availability cluster architecture with multi-AZ control planes, GitOps-powered CI/CD pipelines, and network topology design.'
          },
          {
            image: "Migration&Modernization.png",
            title: 'Migration & Modernization',
            description: 'VM/VMware to Kubernetes migration, on-prem to cloud-managed Kubernetes, and service mesh integration.'
          },
          {
            image: "Managed-Kubernetes.png",
            title: 'Managed Kubernetes & Day-2 Operations',
            description: '24/7 cluster health monitoring, automated patching, backup & disaster recovery, and cost optimization.'
          },
          {
            image: "Training&Knowledge.png",
            title: 'Training & Knowledge Transfer',
            description: 'Hands-on workshops, custom runbooks, and ongoing DevOps mentorship for autonomous cluster management.'
          }
        ]
      },
      {
        title: 'Multi-Regional and Multi-Zonal Architecture Expertise',
        background:'white',
        type: 'grid-cards',
        grid_cards_content: [
          {
            image: "DistributedControlPlane.png",
            title: 'Distributed Control Plane & Data Plane',
            description: 'Highly available Kubernetes environments with distributed control planes across three or more availability zones.',
            features: [
              'Multi-zonal control planes',
              'Automated etcd clustering',
              'Cross-region replication'
            ]
          },
          {
            image: "GlobalLoadBalancing.png",
            title: 'Global Load Balancing and Traffic Steering',
            description: 'DNS-based global load balancers and Anycast ingress solutions for nearest healthy region routing.',
            features: [
              'Global load balancers',
              'Health-check probes',
              'Rapid failover capabilities'
            ]
          },
          {
            image: "Data LocalityandStateful.png",
            title: 'Data Locality and Stateful Workloads',
            description: 'Multi-regional storage replication and persistent volume claims bound to regional storage classes.',
            features: [
              'Cross-region storage replication',
              'Regional storage classes',
              'Application-level caching'
            ]
          }
        ]
      }
    ]
  },
  faq: {
    title: 'Kubernetes Consulting FAQ',
    faqs: [
      {
        question: 'How long does it take to deploy a production-grade Kubernetes cluster?',
        answer: 'With our prebuilt modules and GitOps pipelines, you can deploy production-grade clusters in days—not weeks. The timeline depends on your specific requirements, but we accelerate time-to-value significantly.'
      },
      {
        question: 'Do you support multi-cloud Kubernetes deployments?',
        answer: 'Yes, we specialize in cloud-agnostic Kubernetes deployments across AWS EKS, GCP GKE, Azure AKS, and on-premise clusters with consistent tooling and best practices.'
      },
      {
        question: 'What makes your Kubernetes consulting different?',
        answer: 'Our automation-first philosophy, proven enterprise experience with 3000+ microservices, and security-by-design approach set us apart. We don\'t just deploy clusters—we build resilient, cost-effective platforms.'
      }
    ]
  }
};

const terraformPulumiService: CompleteServiceData = {
  hero: {
    icon: '/assets/services/terraform-pulumi-service.svg',
    category: 'Infrastructure',
    title: 'Terraform and Pulumi Consulting',
    subtitle: 'Infrastructure-as-Code Excellence',
    description: 'Automate and scale your cloud infrastructure with battle-tested IaC practices using Terraform and Pulumi as core engines.',
    highlights: ['Modular Templates', 'Multi-Cloud Expertise', 'CI/CD Integration', 'Security Built-In'],
    leftImage: '/assets/services/terraform-left-image.png',
    rightImage: '/assets/services/terraform-right-image.png'
  },
  content: {
    sections: [
      {
        title: 'Why ReventLabs for IaC with Terraform & Pulumi?',
        type: 'info-cards',
        info_cards_content: [
          {
            id: 'card1',
            number: '01',
            title: 'Modular, Prebuilt Terraform Templates & Terragrunt Best Practices',
            description: 'We maintain a library of reusable Terraform modules encapsulating networking, compute, storage, and security patterns.',
            features: ['Reusable modules', 'DRY principles', 'Cross-account workflows'],
            bgColor: 'white',
            image: '/assets/allServices/Modular.png'
          },
          {
            id: 'card2',
            number: '02',
            title: 'Multi-Cloud & Hybrid Expertise',
            description: 'Create cross-platform Pulumi stacks and Terraform configurations that map services while preserving feature parity.',
            features: ['AWS, GCP, Azure support', 'Service parity mapping', 'Hybrid cloud ready'],
            bgColor: 'white',
            image: '/assets/allServices/Multi-Cloud.png'
          },
          {
            id:'card3',
            number: '03',
            title: 'End-to-End CI/CD Integration',
            description: 'Terraform plans and applies directly tied to pull requests with automated peer review and version control.',
            features: ['Pull request automation', 'Peer review enforced', 'Audit trails'],
            bgColor: 'white',
            image: '/assets/allServices/End-to-End.png'
          }
        ]
      },
      {
        title: 'Why Partner with Us for IaC Consulting?',
        type: 'animated-info-cards',
        animated_info_cards_content: [
          {
            id: 'card1',
            number: '01',
            title: 'Rapid, Consistent Environment Provisioning',
            description: 'Provision identical dev, staging, and prod environments in minutes using reusable modules and automation tools.',
            features: [
              'Terraform and Pulumi module standardization',
              'Terragrunt-based multi-environment reuse',
              'Reduced human error through automation'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Rapid.png'
          },
          {
            id: 'card2',
            number: '02',
            title: 'Pull-Request-Driven Infrastructure Delivery',
            description: 'Peer-reviewed, auditable infrastructure updates triggered by pull requests ensure safe and trackable changes.',
            features: [
              'Automated terraform plan & apply via PRs',
              'CI/CD integrations with Atlantis, TFC, or GitHub Actions',
              'Audit trails for every change'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Pull-Request-Driven.png'
          },
          {
            id: 'card3',
            number: '03',
            title: 'Continuous Drift Detection & Automated Remediation',
            description: 'Monitor and correct any deviation from declared IaC state to keep cloud environments aligned.',
            features: [
              'Scheduled drift checks',
              'Alerts for configuration mismatch',
              'Automated state remediation pipelines'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Continuous-Drift.png'
          },
          {
            id: 'card4',
            number: '04',
            title: 'Built-In Security & Compliance',
            description: 'Policy-as-code and encrypted secrets management ensure compliance and secure deployments.',
            features: [
              'Sentinel or Checkov for policy-as-code',
              'Least-privilege IAM embedded in modules',
              'Vault/KMS integration for secrets'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Security.png'
          },
          {
            id: 'card5',
            number: '05',
            title: 'Scalable, Maintainable Codebases',
            description: 'Modular design enables seamless updates and low maintenance across evolving infrastructure.',
            features: [
              'Version-controlled reusable modules',
              'Support for new cloud features via single-module updates',
              'Reduced technical debt'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Maintainable-Codebases.png'
          },
          {
            id: 'card6',
            number: '06',
            title: 'Importing Existing Infrastructure into IaC',
            description: 'Easily onboard unmanaged cloud resources into IaC without disruption using automation.',
            features: [
              'Automated scanning of AWS/GCP/Azure',
              'Skeleton code generation for existing resources',
              'Safe IaC onboarding for live systems'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Infrastructure-into-IaC.png'
          }
        ]
      }      
    ]
  },
  faq: {
    title: 'Terraform and Pulumi Consulting FAQ',
    faqs: [
      {
        question: 'How long does it take to implement Infrastructure-as-Code across our environments?',
        answer: 'With our battle-tested Terraform and Pulumi module libraries, most teams have production infrastructure codified within 2–4 weeks. We start with your highest-impact environments and expand coverage iteratively, so you see value from the first sprint.'
      },
      {
        question: 'Should we choose Terraform or Pulumi?',
        answer: 'It depends on your team and ecosystem. Terraform\'s declarative HCL is ideal for platform teams standardizing across clouds, while Pulumi lets developers define infrastructure in TypeScript, Python, or Go. We assess your workflows and recommend the right tool for each layer.'
      },
      {
        question: 'Can you migrate our existing manually provisioned infrastructure to code?',
        answer: 'Yes. We import existing cloud resources into Terraform or Pulumi state, refactor them into reusable modules, and put guardrails in place so all future changes flow through version-controlled, peer-reviewed pipelines.'
      }
    ]
  }
};

const sreConsultingService: CompleteServiceData = {
  hero: {
    icon: '/assets/services/sre-consulting-services.svg',
    category: 'Reliability',
    title: 'SRE Consulting Services',
    subtitle: 'Site Reliability Engineering Built for Scale, Speed, and Stability',
    description: 'Build reliable, observable, and scalable systems using modern SRE practices that blend engineering, automation, and DevOps principles.',
    highlights: ['Production Readiness', 'Observability Stack', 'Incident Management', 'Release Engineering'],
    leftImage: '/assets/services/sre-left-image.png',
    rightImage: '/assets/services/sre-right-image.png'
  },
  content: {
    sections: [
      {
        title: 'What We Offer',
        type: 'info-cards',
        info_cards_content: [
          {
            id:'card1',
            number:'01',
            title: 'Production Readiness & Resilience Engineering',
            description: 'Design high-availability and failover strategies across regions using Kubernetes, service mesh, and SLO-driven frameworks.',
            features: ['Multi-region HA', 'SLO frameworks', 'Failover strategies'],
            bgColor: 'white',
            image: '/assets/allServices/Production-Readiness.png'
          },
          {
            id:'card2',
            number:'02',
            title: 'Observability Architecture',
            description: 'Full-stack observability using Prometheus, Grafana, ELK, OpenTelemetry, and custom dashboards.',
            features: ['Metrics and traces', 'Golden signal alerting', 'Faster MTTR'],
            bgColor: 'white',
            image: '/assets/allServices/Observability.png'
          },
          {
            id: 'card3',
            number:'03',
            title: 'Incident Management & Automation',
            description: 'Streamline incident response with auto-alert routing, runbooks, and RCA frameworks.',
            features: ['Auto-alert routing', 'Runbook automation', 'RCA templates'],
            bgColor: 'white',
            image: '/assets/allServices/Incident-Management.png'
          }
        ]
      },
      {
        title: 'Why Partner with Us for SRE Consulting?',
        background:'blue',
        type: 'benefits-card',
        benefits_card_content: [
          {
            image: "ProvenScale.png",
            title: 'Proven Scale & Performance Expertise',
            description: 'We support platforms handling billions of API calls and millions of users with production-tested strategies.',
            features: [
              'Experience with 40B+ API hits/month',
              'Support for 100M+ users and petabyte-scale systems',
              'Production-hardened solutions'
            ]
          },
          {
            image: "Full-StackSRE.png",
            title: 'Full-Stack SRE Capabilities',
            description: 'From infrastructure reliability to user-facing performance, we provide complete SRE coverage.',
            features: [
              'Infrastructure-to-UX observability',
              'End-to-end performance optimization',
              'On-call reliability improvements'
            ]
          },
          {
            image: "Cloud-Native.png",
            title: 'Cloud-Native, Cross-Platform Architecture',
            description: 'Design and optimize SRE systems across clouds using container-native best practices.',
            features: [
              'Cross-cloud SRE patterns',
              'Container-native monitoring and resilience',
              'Seamless multi-environment support'
            ]
          },
          {
            image: "SLOTracking.png",
            title: 'SLO Tracking & Intelligent Dashboards',
            description: 'Automate error budget tracking and observability via real-time dashboards and alerting systems.',
            features: [
              'Automated SLO/SLA monitoring',
              'Custom SRE dashboards',
              'Real-time error budget analytics'
            ]
          },
          {
            image: "ChaosEngineering.png",
            title: 'Chaos Engineering & Debugging Playbooks',
            description: 'Equip your teams with structured incident response and resilience engineering toolkits.',
            features: [
              'Ready-to-use debugging playbooks',
              'Chaos engineering framework integration',
              'On-call fatigue reduction strategies'
            ]
          }
        ]
      },
      {
        title: 'Supported Tools & Ecosystem',
        type: 'tools',
        tools: [
          {
            subheading: "Observability & Monitoring",
            text: "Gain deep visibility into your systems with our advanced observability stack. We help set up monitoring, tracing, and logging to ensure system health, fast debugging, and improved reliability.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "observability-monitoring.png"
          },
          {
            subheading: "Deployment & GitOps",
            text: "Empower your delivery pipeline with GitOps and declarative deployments. We automate complex rollouts, simplify configurations, and ensure repeatable, version-controlled deployments.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "deployment-gitOps.png"
          },
          {
            subheading: "CI/CD Pipelines",
            text: "Accelerate your development lifecycle with modern CI/CD pipelines. We build fast, secure, and scalable delivery workflows tailored to your engineering needs.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "cicd-pipeline.png"
          },
          {
            subheading: "Infrastructure as Code & Automation",
            text: "Define, manage, and automate your infrastructure at scale. Our IaC expertise ensures consistency, traceability, and efficiency across cloud environments.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "infrastructureAsCode.png"
          },
          {
            subheading: "Incident Management",
            text: "Ensure rapid incident response with integrated management tools. We help set up alert routing, on-call rotations, and communication workflows to minimize downtime.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "incidentManagement.png"
          },
          {
            subheading: "Reliability Tooling",
            text: "Boost system resilience with proactive reliability tools. From auto-scaling to chaos engineering and security scanning, we prepare your stack for real-world failures.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "reliabilityTooling.png"
          },
          {
            subheading: "Secrets & Config Management",
            text: "Secure and streamline your configuration and secrets management. We implement solutions to safely handle sensitive data across all environments.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "secretAndConfigs.png"
          }
        ]
      },
      {
        title: 'Action Plan: Our SRE Engagement Process',
        background:'blue',
        type: 'process',
        processSteps: [
          {
            step: 1,
            title: 'Platform Assessment & SLO Definition',
            description: 'Analyze current reliability posture, incidents, and toil; define SLIs, SLOs, and error budgets; and map risk profiles to business outcomes.'
          },
          {
            step: 2,
            title: 'Observability & Alerting Foundation',
            description: 'Instrument key services with OpenTelemetry, Prometheus, and Grafana; standardize alerting using golden signals; and create centralized SRE dashboards.'
          },
          {
            step: 3,
            title: 'Release Engineering & GitOps',
            description: 'Re-architect release workflows with ArgoCD, Argo Rollouts, and Helm; enable progressive delivery; and set up CI/CD gates and test automation.'
          },
          {
            step: 4,
            title: 'Resilience Design & Scaling',
            description: 'Implement auto-scaling, pod disruption budgets, and fault tolerance across AZs; use KEDA/HPA/VPA for elasticity; and perform chaos testing.'
          },
          {
            step: 5,
            title: 'Incident Response & Reliability Automation',
            description: 'Automate incident response with Slack & PagerDuty; document runbooks and RCA workflows; and conduct game days and SRE ceremonies.'
          }
        ]
      }       
    ]
  },
  industryUseCases: {
    title: 'Industries & Use Cases We Power with SRE Expertise',
    subtitle: 'Our production-tested Site Reliability Engineering solutions enable performance, availability, and cost control across critical industries.',
    useCases: [
      {
        icon: '🌐',
        title: 'SaaS Platforms',
        description: 'Ensure high availability across multiple regions with failover strategies, traffic routing, and zero-downtime deployments.',
        features: [
          'Multi-region high availability',
          'Failover and traffic routing',
          'Zero-downtime rollouts'
        ]
      },
      {
        icon: '🧠',
        title: 'ML Workloads',
        description: 'Enable GPU fleet autoscaling, enforce SLA-driven data pipelines, and reduce model training failures via robust observability.',
        features: [
          'GPU autoscaling',
          'Data pipeline SLA enforcement',
          'Model training reliability'
        ]
      },
      {
        icon: '🛍️',
        title: 'E-commerce Platforms',
        description: 'Optimize for low latency, handle seasonal load like Black Friday, and reduce cart abandonment with resilient APIs and performance tuning.',
        features: [
          'Low-latency optimization',
          'Black Friday scaling strategies',
          'Cart abandonment reduction'
        ]
      },
      {
        icon: '💳',
        title: 'Fintech Applications',
        description: 'Achieve regulatory-grade uptime, maintain detailed incident traceability, and integrate secure observability pipelines.',
        features: [
          'Regulatory uptime guarantees',
          'Incident traceability',
          'Secure observability'
        ]
      }
    ]
  },
  faq: {
    title: 'SRE Consulting Services FAQ',
    faqs: [
      {
        question: 'How do you improve reliability without slowing down our releases?',
        answer: 'We implement SLOs and error budgets that give teams a shared, data-driven definition of reliability. You ship fast when error budgets are healthy and prioritize stability when they aren\'t—so reliability and velocity stop competing with each other.'
      },
      {
        question: 'Do you work with our existing on-call and incident processes?',
        answer: 'Yes. We assess your current tooling and runbooks, then strengthen them with structured incident response, blameless postmortems, and automation that reduces toil—rather than forcing a disruptive process change on your teams.'
      },
      {
        question: 'What results can we expect from SRE consulting?',
        answer: 'Clients typically see significantly reduced MTTR, fewer repeat incidents, and measurable drops in operational toil within the first quarter. We define success metrics up front and report progress against them throughout the engagement.'
      }
    ]
  }
};

const prometheusGrafanaService: CompleteServiceData = {
  hero: {
    icon: '/assets/services/prometheus-service.svg',
    category: 'Monitoring',
    title: 'Prometheus and Grafana Consulting',
    subtitle: 'Advanced Monitoring & Observability',
    description: 'Turnkey observability platform with real-time metrics, advanced alerting, and rich dashboards for proactive troubleshooting.',
    highlights: ['High-Scale Deployments', 'SLO Management', 'Long-Term Storage', 'Multi-Cloud Monitoring'],
    leftImage: '/assets/services/prometheus-left-image.png',
    rightImage: '/assets/services/prometheus-right-image.png'
  },
  content: {
    sections: [
      {
        title: 'Why ReventLabs for Prometheus & Grafana',
        background:'blue',
        type: 'benefits',
        content: [
          {
            title: 'Proven High-Scale Deployments',
            description: 'Built and operated Prometheus federation across clusters handling 40B+ API hits per month.',
            features: ['40B+ API hits handled', 'Low-latency ingestion', 'Sub-second dashboards']
          },
          {
            title: 'Cloud-Native & Multi-Cloud Expertise',
            description: 'Implement agent-based metric collection across EKS, AKS, GKE, or bare-metal Kubernetes.',
            features: ['Multi-cloud support', 'BYOC deployment', 'Consistent instrumentation']
          }
        ]
      },
      {
        title: 'Core Prometheus & Grafana Stack Offerings',
        type: 'info-cards',
        info_cards_content: [
          {
            id:'card1',
            number: '01',
            title: 'Monitoring Architecture Design & Deployment',
            description: 'Design scalable and resilient Prometheus setups with long-term storage, automated provisioning, and centralized Grafana dashboards for multi-tenant visibility.',
            features: [
              'Prometheus federation & remote-write to Cortex/Thanos/Mimir',
              'Scalable TSDB with object storage and compaction tuning',
              'Helm/Terraform-based provisioning of exporters and operators',
              'SSO-enabled multi-tenant Grafana deployments with prebuilt dashboards'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Monitoring-Architecture.png'
          },
          {
            id: 'card2',
            number: '02',
            title: 'Advanced Alerting & SLO Management',
            description: 'Set up highly available Alertmanager clusters with intelligent routing, suppression logic, and Grafana-based SLO dashboards to track error budgets and service health.',
            features: [
              'HA Alertmanager with custom routing trees',
              'CI-integrated alert silence automation',
              'Grafana SLO dashboards with burn rate & threshold insights',
              'Alert integrations with PagerDuty, Opsgenie, Slack'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Advanced-Alerting.png'
          },
          {
            id: 'card3',
            number: '03',
            title: 'Kubernetes-Specific Monitoring & Troubleshooting',
            description: 'Enable deep observability into Kubernetes clusters with automated pod metrics scraping, autoscaling insights, and correlation across metrics, logs, and traces.',
            features: [
              'ServiceMonitor-based pod and control-plane metrics collection',
              'Cluster autoscaling dashboards (HPA & resource usage)',
              'Grafana Explore: integrated logs, metrics, and traces',
              'OpenTelemetry and EFK/ELK stack integration'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Kubernetes-Specific.png'
          },
          {
            id: 'card4',
            number: '04',
            title: 'Managed Monitoring & Ongoing Support',
            description: 'Get end-to-end monitoring coverage with 24/7 support, proactive health checks, upgrade management, and custom Grafana visualizations tailored to your KPIs.',
            features: [
              'Target health checks and WAL/compaction monitors',
              'TSDB retention and query latency optimization',
              'Safe upgrade rollouts with backup & validation',
              'Custom Grafana plugins and reusable dashboard templates'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Managed-Monitoring.png'
          }
        ]
      },
      {
        title: 'Expertise in High-Scale, Multi-Regional Monitoring',
        type: 'animated-info-cards',
        animated_info_cards_content: [
          {
            id: 'card1',
            number: '01',
            title: 'Federated Metric Aggregation Across Regions',
            description: 'Architect multi-region Prometheus with Thanos Querier to enable unified global queries while retaining region-specific data sovereignty and retention.',
            features: [
              'Prometheus Federv2 regional clusters',
              'Thanos Querier for global data aggregation',
              'Seamless multi-day, multi-region metric queries'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Federated-Metric.png'
          },
          {
            id: 'card2',
            number: '02',
            title: 'Anycast Ingress & Global Alert Routing',
            description: 'Ensure global availability and alert reliability with DNS Anycast or cloud-native ingress plus regional fallback webhooks to handle alert delivery even during outages.',
            features: [
              'DNS Anycast or global load balancer for ingestion endpoints',
              'Region-based alert routing with failover receivers',
              'High resilience for alert delivery across failure domains'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Anycast-Ingress.png'
          },
          {
            id: 'card3',
            number: '03',
            title: 'Disaster Recovery & Cross-Cluster Failover',
            description: 'Enable automatic failover and data continuity by redirecting remote-write traffic and Grafana dashboards to secondary Prometheus clusters during region-level outages.',
            features: [
              'Cross-region Prometheus failover setup',
              'Auto-switching Grafana data sources',
              'Continuous observability during regional disasters'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Disaster-Recovery.png'
          },
          {
            id: 'card4',
            number: '04',
            title: 'Multi-Cloud Metric Harmonization',
            description: 'Normalize metrics across AWS, GCP, and Azure to create unified dashboards showing combined service performance across cloud providers.',
            features: [
              'Cross-cloud metric name and label normalization',
              'Unified dashboards across AWS, GCP, and Azure',
              'Support for VM, Kubernetes, and managed service metrics'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Multi-Cloud.png'
          }
        ]
      },
      {
        title: 'Why Partner with Us for Prometheus & Grafana Stack Consulting?',
        background:'blue',
        type: 'benefits-card',
        benefits_card_content: [
          {
            image: "Developer&SRE.png",
            title: 'End-to-End Visibility',
            description: 'Unified dashboards across infrastructure, clusters, and applications accelerate root cause analysis.',
            features: [
              'Infrastructure, cluster, and app metrics in one place',
              'Faster root cause identification',
              'Customizable visualizations'
            ]
          },
          {
            image: "RapidAlerting.png",
            title: 'Rapid Alerting & Precise Escalation',
            description: 'Smart alerting routes only actionable incidents to the right on-call engineers.',
            features: [
              'Sophisticated alerting rules',
              'Dynamic routing logic',
              'Reduced alert fatigue'
            ]
          },
          {
            image: "Cost-OptimizedStorage.png",
            title: 'Cost-Optimized Storage',
            description: 'Tiered time-series storage balances performance with long-term cost savings.',
            features: [
              'Hot/warm/cold TSDB storage architecture',
              'Optimized S3/GCS costs',
              'Fast queries for recent metrics'
            ]
          },
          {
            image: "SeamlessMulti-Cloud.png",
            title: 'Seamless Multi-Cloud Operations',
            description: 'Monitor environments across any cloud or on-prem with consistent patterns.',
            features: [
              'Works with EKS, AKS, GKE, and on-prem Kubernetes',
              'Avoids vendor lock-in',
              'Unified cross-cloud observability'
            ]
          },
          {
            image: "Security&Compliance.png",
            title: 'Security & Compliance Assurance',
            description: 'Built-in security practices meet industry compliance standards effortlessly.',
            features: [
              'TLS-encrypted metric scrapes',
              'Ephemeral RBAC tokens for exporters',
              'Drift detection for config compliance'
            ]
          },
          {
            image: "Developer&SRE.png",
            title: 'Developer & SRE Efficiency',
            description: 'Automated workflows minimize manual effort and boost engineering productivity.',
            features: [
              'Helm-based upgrades',
              'GitOps for dashboard versioning',
              'Automated provisioning of monitoring stack'
            ]
          }
        ]
      }                  
    ]
  },
  faq: {
    title: 'Prometheus and Grafana Consulting FAQ',
    faqs: [
      {
        question: 'How long does it take to stand up a production-grade monitoring stack?',
        answer: 'Most Prometheus and Grafana deployments are live within 1–2 weeks, including dashboards, alert rules, and integrations with your existing infrastructure. Larger multi-cluster rollouts with long-term storage typically take 3–4 weeks.'
      },
      {
        question: 'Can you scale Prometheus for large, multi-cluster environments?',
        answer: 'Yes. We design federated and long-term storage architectures using Thanos or Mimir, enabling unified querying, high availability, and cost-efficient retention across clusters and regions.'
      },
      {
        question: 'Will you help reduce alert noise and dashboard sprawl?',
        answer: 'Absolutely. We audit existing alerts and dashboards, consolidate them around SLOs and actionable signals, and ensure your on-call engineers are paged only when something truly needs human attention.'
      }
    ]
  }
};

// Policy-as-Code Services
const policyAsCodeService: CompleteServiceData = {
  hero: {
    icon: '/assets/services/policy-as-code-service.svg',
    category: 'Governance',
    title: 'Policy-as-Code Services',
    subtitle: 'Automated Governance & Compliance',
    description: 'Implement comprehensive policy automation across your infrastructure, Kubernetes, CI/CD, and application delivery pipelines.',
      highlights: ['Multi-Layer Coverage', 'Automation-First', 'Compliance Integration', 'Real-Time Enforcement'],
    leftImage: '/assets/services/policy-as-code-left-image.png',
    rightImage: '/assets/services/policy-as-code-right-image.png'
  },
  content: {
    sections: [
      {
        title: 'Multi-Layered Policy Coverage',
        background:'blue',
        type: 'process',
        processSteps: [
          {
            title: 'Infrastructure-as-Code (Terraform/Terragrunt)',
            description: 'Use OPA with Conftest and Checkov to enforce guardrails across Terraform modules.'
          },
          {
            title: 'Kubernetes Platform Policies',
            description: 'Real-time policy validation using Kyverno, OPA/Gatekeeper, and Kubewarden.'
          },
          {
            title: 'CI/CD Pipeline Guardrails',
            description: 'Integrated policy engines ensure code cannot ship without satisfying pre-deployment gates.'
          },
          {
            title: 'Application Layer & Runtime',
            description: 'Input validation via Rego policies and runtime verification hooks.'
          }
        ]
      },
      {
        title: 'The Measurable Impact of Automated Governance',
        type:'rich-text',
        rich_text_content:[
          {
            percent : 40,
            barColor: "#10B981",
            topic: "Average Cost Reduction"
          },
          {
            percent : 95,
            barColor: "#3B82F6",
            topic: "Cost Visibility Increase"
          },
          {
            percent : 60,
            barColor: "#8B5CF6",
            topic: "Resource Efficiency Gain"
          }
        ]
      },
      {
        title: 'What You Get',
        background:'white',
        type: 'features',
        features_content:[
          {
            text: "Real-time cost visibility across multi-cloud environments"
          },
          {
           text: "Team-level cost ownership and attribution"
          },
          {
            text: "Automated governance and policy enforcement"
          },
          {
            text: "Developer-integrated cost awareness tools"
          }
        ]
      }
    ]
  },
  faq: {
    title: 'Policy-as-Code Services FAQ',
    faqs: [
      {
        question: 'What is Policy-as-Code and why does it matter?',
        answer: 'Policy-as-Code turns your security, compliance, and cost rules into version-controlled code that is enforced automatically in CI/CD pipelines and at runtime. Instead of catching violations in audits, you prevent them before deployment.'
      },
      {
        question: 'Which policy engines and tools do you implement?',
        answer: 'We work with Open Policy Agent (OPA), Gatekeeper, Kyverno, Sentinel, and cloud-native policy engines—selected to fit your stack across Terraform, Kubernetes, and CI/CD workflows.'
      },
      {
        question: 'Can Policy-as-Code help with compliance frameworks like SOC 2 or PCI DSS?',
        answer: 'Yes. We map policies directly to control requirements for SOC 2, HIPAA, GDPR, ISO 27001, and PCI DSS, generating continuous, audit-ready evidence instead of point-in-time snapshots.'
      },
      {
        question: 'Will policy enforcement slow down our developers?',
        answer: 'No—done right, it speeds them up. Policies run as fast, automated checks with clear feedback in pull requests, so developers fix issues in minutes rather than discovering them in production or during audits.'
      }
    ]
  },
};

// GitOps Consulting & Implementation Services
const gitopsService: CompleteServiceData = {
  hero: {
    icon: '/assets/services/gitops-service.png',
    category: 'DevOps',
    title: 'GitOps Implementation Services',
    subtitle: 'Build Reliable, Auditable, and Fast Software Delivery with GitOps',
    description: 'Modernize Kubernetes delivery workflows using GitOps principles for predictability, automation, and visibility.',
    highlights: ['Progressive Delivery', 'Multi-Cluster Management', 'Security-First', 'Automated Rollback'],
    leftImage: '/assets/services/github-left-image.png',
    rightImage: '/assets/services/github-right-image.png'
  },
  content: {
    sections: [
      {
        title: 'Why Partner with a GitOps Consulting Expert?',
        type: 'info-cards',
        info_cards_content: [
          {
            id: 'card1',
            number: '01',
            title: 'Declarative Everything',
            description: 'Define infrastructure, applications, environments, and policies declaratively for reproducible environments.',
            features: ['Version-controlled environments', 'Reproducible deployments', 'Scale with your team'],
            bgColor: 'white',
            image: '/assets/allServices/Declarative-Everything.png'
          },
          {
            id: 'card2',
            number : '02',
            title: 'Developer Experience & Flow',
            description: 'Free developers from manual scripts and enable safe, quick, independent deployment.',
            features: ['Eliminate manual scripts', 'Independent deployments', 'Consistent staging'],
            bgColor: 'white',
            image: '/assets/allServices/Flow.png'
          },
          {
            id: 'card3',
            number : '03',
            title: 'Faster, Safer Releases',
            description: 'Deliver changes to production several times a day using Git-based workflows and progressive delivery.',
            features: ['Multiple daily deployments', 'Progressive delivery', 'Automated validation'],
            bgColor: 'white',
            image: '/assets/allServices/Faster-Safer-Releases.png'
          }
        ]
      },
      {
        title: 'What We Offer as GitOps Consulting & Implementation Experts',
        type: 'animated-info-cards',
        animated_info_cards_content: [
          {
            id: 'card1',
            number: '01',
            title: 'GitOps Advisory & Strategy Services',
            description: 'Consulting and roadmap planning to transition teams from CI/CD to GitOps with tailored adoption blueprints.',
            features: [
              'Infrastructure maturity assessment',
              'Custom GitOps adoption blueprints',
              'Tool selection and Git structure guidance',
              'Automation and security alignment'
            ],
            bgColor: 'white',
            image: '/assets/allServices/GitOps-Advisory.png'
          },
          {
            id: 'card2',
            number: '02',
            title: 'GitOps Implementation – From Plan to Production',
            description: 'Build or migrate GitOps pipelines using Argo CD, FluxCD, Helm, and Kustomize for declarative, automated workflows.',
            features: [
              'Git-backed deployment pipelines',
              'Drift detection and rollback support',
              'Infrastructure-as-code and secure secrets integration',
              'Cluster lifecycle & app delivery automation'
            ],
            bgColor: 'white',
            image: '/assets/allServices/GitOps-Implementation.png'
          },
          {
            id: 'card3',
            number: '03',
            title: 'Enterprise-Grade GitOps Support',
            description: 'Support multi-cluster environments with real-time monitoring, incident response, and scaling assistance.',
            features: [
              'Multi-cluster ArgoCD ApplicationSets management',
              'Real-time GitOps monitoring',
              'Incident response and tool upgrades',
              'Cross-region GitOps scaling'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Enterprise-Grade.png'
          },
          {
            id: 'card4',
            number: '04',
            title: 'GitOps Enablement & Training',
            description: 'Workshops and enablement sessions to make teams self-sufficient in GitOps fundamentals and tools.',
            features: [
              'Pull-based deployment model training',
              'Drift correction and progressive rollout',
              'Hands-on tool workshops: Flux, Helm, Terraform, Argo Rollouts',
              'Team enablement for independent releases'
            ],
            bgColor: 'white',
            image: '/assets/allServices/GitOps-Enablement.png'
          }
        ]
      },
      {
        title: 'Comprehensive GitOps Consulting Offerings',
        background:'blue',
        type: 'process',
        processSteps: [
          {
            title: 'GitOps Strategy & Roadmap',
            description: 'Evaluate delivery architecture and design tailored GitOps strategy aligned with product goals, compliance, and team structure; decisions on mono-repo vs multi-repo, promotion workflows, and branch strategies.'
          },
          {
            title: 'Toolchain Implementation & CI Integration',
            description: 'Implement GitOps pipelines with Argo CD, FluxCD, Helm, Kustomize, and Harness; integrate with GitHub, GitLab, Bitbucket, and CI tools like GitHub Actions, Jenkins, CircleCI to automate builds, tests, and deployments.'
          },
          {
            title: 'Progressive Delivery with Argo Rollouts',
            description: 'Enable canary, blue-green, and experimental rollouts with traffic shifting, metric analysis (Prometheus, Datadog), automatic rollback; support trunk-based development for fast, auditable production releases.'
          },
          {
            title: 'GitOps Security & Compliance',
            description: 'Embed security best practices: encrypted secrets with Vault, Secret Manager, SOPS; dynamic secrets injection; enforce RBAC and SSO via OIDC; implement approval gates and policy-as-code with OPA/Gatekeeper to align with SOC2, HIPAA, ISO27001.'
          },
          {
            title: 'Multi-Cluster, Multi-Region Deployment',
            description: 'Orchestrate Git-driven deployments across regions and clouds using Argo CD ApplicationSets, GKE Fleet, Terraform+GitOps; provide unified control plane, service discovery, config consistency, and compliance across clusters.'
          },
          {
            title: 'Enablement, Dashboards & Training',
            description: 'Deliver Slack-integrated dashboards, real-time rollout monitors, Kubecost integration, and structured training to empower teams for autonomous GitOps pipeline ownership.'
          },
          {
            title: 'Infrastructure GitOps',
            description: 'Extend GitOps to infrastructure management via Terraform workflows using Atlantis, Terragrunt, Terramate; enable cloud infra changes through PRs, enforce plan-and-apply reviews, automate drift detection, and manage multi-cloud infra securely.'
          }
        ]
      },
      {
        title: 'Supported Tools & Ecosystem',
        type: 'tools',
        tools: [
          {
            subheading: "GitOps Engines",
            text: "Automate and manage Kubernetes deployments with reliable GitOps engines for continuous delivery and synchronization.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "GitOpsEngines.png"
          },
          {
            subheading: "Templating & Config Management",
            text: "Simplify Kubernetes app configuration and management using powerful templating and configuration tools.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "Templating&ConfigManagement.png"
          },
          {
            subheading: "Version Control Systems",
            text: "Maintain your codebase with trusted version control platforms enabling collaboration and source management.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "VersionControlSystems.png"
          },
          {
            subheading: "CI Integration",
            text: "Integrate continuous integration pipelines to automate testing and build processes seamlessly.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "CI-Integration.png"
          },
          {
            subheading: "Progressive Delivery",
            text: "Implement canary releases, blue-green deployments, and other progressive delivery strategies for safer rollouts.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "ProgressiveDelivery.png"
          },
          {
            subheading: "Infrastructure as Code",
            text: "Provision and manage infrastructure declaratively to ensure reproducible and scalable environments.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "Infrastructure-as-Code.png"
          },
          {
            subheading: "Secrets Management",
            text: "Securely store, manage, and inject secrets and sensitive configuration across your applications and infrastructure.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "SecretsManagement.png"
          },
          {
            subheading: "Security & Policy Enforcement",
            text: "Enforce security policies and compliance through policy-as-code frameworks integrated with your workflows.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "Security&PolicyEnforcement.png"
          },
          {
            subheading: "Drift Detection & Auditing",
            text: "Detect configuration drift and maintain audit trails to ensure infrastructure consistency and compliance.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "DriftDetection&Auditing.png"
          }
        ]
      }            
    ]
  },
  faq: {
    title: 'GitOps Implementation Services FAQ',
    faqs: [
      {
        question: 'How long does a GitOps implementation take?',
        answer: 'A production-ready GitOps pipeline with Argo CD or Flux is typically live within 2–3 weeks. We start with a pilot application, prove the workflow end-to-end, then roll it out across your services and environments.'
      },
      {
        question: 'Argo CD or Flux—which should we use?',
        answer: 'Both are excellent CNCF-graduated tools. Argo CD\'s UI and multi-cluster application management suit platform teams, while Flux\'s composable controllers integrate deeply with existing toolchains. We recommend based on your team\'s workflows, scale, and security requirements.'
      },
      {
        question: 'How does GitOps improve security and auditability?',
        answer: 'Every change to your infrastructure and applications becomes a version-controlled, peer-reviewed commit with full history. Drift is detected and reconciled automatically, and rollbacks are as simple as reverting a commit—giving you a complete audit trail by default.'
      }
    ]
  }
};

// Infrastructure Design & Delivery
const infrastructureDesignService: CompleteServiceData = {
  hero: {
    icon: '/assets/services/in.png',
    category: 'Architecture',
    title: 'Infrastructure Design & Delivery',
    subtitle: 'Architected for Least Cost, Maximum Security & Reliability',
    description: 'Build infrastructure that\'s scalable, secure, and ready for growth with cloud-native, serverless, and automation-first platforms.',
    highlights: ['Cloud-Native Design', 'Security Built-In', 'Scalability Focus', 'End-to-End Implementation'],
    leftImage: '/assets/services/infrastructure-left-image.png',
    rightImage: '/assets/services/infrastructure-right-image.png'
  },  
  content: {
    sections: [
      {
        title: 'What We Offer',
        type: 'info-cards',
        info_cards_content: [
          {
            id: 'card1',
            number: '01',
            title: 'Cloud-Native & Serverless Infrastructure Architecture',
            description: 'Design infrastructure that fits your application architecture using microservices, serverless, and event-driven systems.',
            features: ['Microservices ready', 'Serverless patterns', 'Event-driven systems'],
            bgColor: 'white',
            image: '/assets/allServices/Cloud-Native.png'
          },
          {
            id: 'card2',
            number: '02',
            title: 'Secure, Compliant & Resilient Foundations',
            description: 'Security and compliance embedded from day zero with least privilege IAM, VPC segmentation, and encryption.',
            features: ['SOC2, ISO27001, HIPAA', 'Least privilege IAM', 'Encryption by default'],
            bgColor: 'white',
            image: '/assets/allServices/Secure.png'
          },
          {
            id: 'card3',
            number: '03',
            title: 'Kubernetes Platform Engineering',
            description: 'Single-region to multi-region clusters with GitOps, progressive delivery, observability, and cost-aware scheduling.',
            features: ['Multi-region clusters', 'GitOps integration', 'Cost optimization'],
            bgColor: 'white',
            image: '/assets/allServices/Kubernetes-Platform.png'
          }
        ]
      },
      {
        title: 'Supported Platforms & Tools',
        type: 'tools',
        tools: [
          {
            subheading: "Cloud",
            text: "Deploy scalable and secure infrastructure across leading public and private cloud providers tailored to your needs.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "cloud.png"
          },
          {
            subheading: "Infrastructure as Code (IaC)",
            text: "Automate infrastructure provisioning and management using modern IaC tools for consistent and repeatable environments.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "infrastructureAsCode.png"
          },
          {
            subheading: "CI/CD",
            text: "Integrate powerful CI/CD pipelines to streamline development workflows and accelerate delivery.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "cicd.png"
          },
          {
            subheading: "Kubernetes",
            text: "Manage containerized workloads with ease using robust Kubernetes platforms for both enterprise and edge deployments.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "Kubernetes.png"
          },
          {
            subheading: "GitOps",
            text: "Achieve declarative, version-controlled deployments with GitOps tools for scalable and reliable operations.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "GitOps.png"
          },
          {
            subheading: "Secrets Management",
            text: "Securely manage sensitive credentials and configurations with centralized secrets management solutions.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "SecretsManagement.png"
          },
          {
            subheading: "Monitoring & Logs",
            text: "Get end-to-end observability and insights with monitoring, logging, and tracing tools across your systems.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "Monitoring&Logs.png"
          },
          {
            subheading: "Security",
            text: "Ensure compliance and protect your infrastructure with identity, access, and policy enforcement tools.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "Security.png"
          },
          {
            subheading: "Serverless",
            text: "Build scalable, event-driven applications using fully managed serverless platforms with minimal operational overhead.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "Serverless.png"
          }
        ]
      },
      {
        title: 'Action Plan to Launch Infrastructure from Scratch',
        background:'blue',
        type: 'process',
        processSteps: [
          {
            title: 'Assessment & Blueprinting',
            description: 'Deep dive into product architecture, business goals, compliance mandates, and expected user growth.'
          },
          {
            title: 'Infrastructure Design',
            description: 'Define service mesh, VPC layout, subnetting strategy, scaling policies, and cloud platform mix (AWS, GCP, Azure).'
          },
          {
            title: 'IaC + GitOps Implementation',
            description: 'Implement modular Terraform/Terragrunt setups, CI/CD pipelines, and GitOps integration for infrastructure and application deployments.'
          },
          {
            title: 'Security Hardening & Compliance Setup',
            description: 'Set up least privilege IAM, secret rotation, audit logging, and pre-configure for SOC2, HIPAA, and ISO27001 compliance.'
          },
          {
            title: 'Observability, Chaos Readiness & Cost Guardrails',
            description: 'Integrate Prometheus monitoring, distributed tracing, budget alerts, autoscaling policies, and conduct simulated outages for resilience.'
          },
          {
            title: 'Documentation, Automation & Year 2 Readiness',
            description: 'Provide complete runbooks, onboarding guides, and workflows to maintain future compliance with minimal overhead, including CI/CD workflows, disaster recovery drills, and cost/access matrix.'
          }
        ]
      },
      {
        title: 'The Impact of Well-Architected Infrastructure',
        type:'rich-text',
        rich_text_content:[
          {
            percent : 40,
            barColor: "#10B981",
            topic: "Average Cost Reduction"
          },
          {
            percent : 95,
            barColor: "#3B82F6",
            topic: "Cost Visibility Increase"
          },
          {
            percent : 60,
            barColor: "#8B5CF6",
            topic: "Resource Efficiency Gain"
          }
        ]
      },
      {
        title: 'What You Get',
        background:'blue',
        type: 'features',
        features_content:[
          {
            text: "Real-time cost visibility across multi-cloud environments"
          },
          {
           text: "Team-level cost ownership and attribution"
          },
          {
            text: "Automated governance and policy enforcement"
          },
          {
            text: "Developer-integrated cost awareness tools"
          }
        ]
      }
    ]
  },
  faq: {
    title: 'Infrastructure Design & Delivery FAQ',
    faqs: [
      {
        question: 'How do you approach designing infrastructure for a new platform?',
        answer: 'We start with your workload requirements, compliance needs, and growth projections, then design a reference architecture covering networking, compute, security, and observability. Everything is delivered as Infrastructure-as-Code with documentation your team owns.'
      },
      {
        question: 'Can you work within our existing cloud environment rather than starting from scratch?',
        answer: 'Yes. Most engagements begin with an assessment of your current architecture. We identify risks and quick wins, then evolve the environment incrementally—no disruptive rip-and-replace required.'
      },
      {
        question: 'How do you ensure the infrastructure you deliver is production-ready?',
        answer: 'Every design is validated against availability, security, cost, and operational criteria before handover. We deliver runbooks, monitoring, and knowledge-transfer sessions so your team can operate the platform confidently from day one.'
      }
    ]
  },
};

// Cloud Migration Services
const cloudMigrationService: CompleteServiceData = {
  hero: {
    icon: '/assets/services/Cloud.png',
    category: 'Migration',
    title: 'Cloud Migration Services',
    subtitle: 'Seamless Cloud Migration & Modernization',
    description: 'Comprehensive, end-to-end solution for moving workloads smoothly and securely to cloud environments with minimal downtime.',
    highlights: ['Zero-Downtime Strategies', 'Multi-Cloud Approaches', 'Legacy Modernization', 'Cost Optimization'],
    leftImage: '/assets/services/cloud-migration-left-image.png',
    rightImage: '/assets/services/cloud-migration-right-image.png'
  },
  content: {
    sections: [
      {
        title: 'Why ReventLabs for Cloud Migration Services?',
        type: 'info-cards',
        info_cards_content: [
          {
            id: 'card1',
            number: '01',
            title: 'Deep Technical Expertise and Certifications',
            description: 'Our team holds AWS, GCP, and Azure certifications with large-scale migration experience.',
            features: ['Multi-cloud certified', 'Enterprise experience', 'Best practices adherence'],
            bgColor: 'white',
            image: '/assets/allServices/Deep-Technical.png'
          },
          {
            id: 'card2',
            number:'02',
            title: 'Automation-First Methodology',
            description: 'Prebuilt, premium modules and templates that streamline migration pipelines.',
            features: ['Battle-tested modules', 'Reproducible workflows', 'Reduced manual steps'],
            bgColor: 'white',
            image: '/assets/allServices/Prebuilt.png'
          },
          {
            id: 'card3',
            number:'03',
            title: 'Comprehensive Assessment and Planning',
            description: 'Stakeholder interviews, architecture reviews, and detailed migration roadmaps.',
            features: ['RTO/RPO targets', 'Interdependency mapping', 'Rollback strategies'],
            bgColor: 'white',
            image: '/assets/allServices/Planning.png'
          }
        ]
      },
      {
        title: 'Cloud Migration Service Offerings',
        background:'blue',
        type: 'benefits-card',
        benefits_card_content: [
          {
            image: "On-PremisestoPublicCloud.png",
            title: 'On-Premises to Public Cloud or BYOC Deployment',
            description: 'Migrate workloads from on-premises data centers to public clouds with our BYOC model.',
            features: ['Data sovereignty preserved', 'Managed services deployment', 'Minimal disruption']
          },
          {
            image: "Inter-CloudPlatform.png",
            title: 'Inter-Cloud Platform Migration',
            description: 'Transition workloads between cloud providers with service parity mapping and automated transformations.',
            features: ['Service parity mapping', 'Automated transformations', 'Zero config drift']
          },
          {
            image: "VM-to-ContainerTransformation.png",
            title: 'VM-to-Container Transformation',
            description: 'Convert legacy applications to containerized microservices with Kubernetes orchestration.',
            features: ['Container optimization', 'Microservices refactoring', 'Auto-scaling policies']
          }
        ]
      }
    ]
  },
  faq: {
    title: 'Migration FAQ',
    faqs: [
      {
        question: 'What could go wrong during a migration, and how does ReventLabs prevent downtime or data loss?',
        answer: 'Risks include unexpected downtime, data inconsistency, and compatibility issues. Our structured approach features phased cutovers, automated data replication with CDC, and rollback plans to ensure workloads stay online.'
      },
      {
        question: 'How long will it take to complete my migration?',
        answer: 'Timeline depends on complexity, data volume, and compliance requirements. Simple lift-and-shift can finish in weeks, while refactoring takes months. We provide detailed roadmaps with Discovery, PoC, Pilot, and Production phases.'
      }
    ]
  }
};

// Cloud Compliance & Security Readiness Services
const complianceSecurityService: CompleteServiceData = {
  hero: {
    icon: '/assets/services/Cloud-Compliance.png',
    category: 'Security',
    title: 'Cloud Compliance & Security Services',
    subtitle: 'End-to-End Compliance Readiness — Without Third-Party SaaS',
    description: 'Become audit-ready for SOC 2, HIPAA, GDPR, ISO 27001, and PCI DSS without relying on expensive SaaS solutions.',
    highlights: ['SOC 2, ISO 27001, HIPAA', 'No SaaS Dependencies', 'Automated Monitoring', 'Security-First'],
    leftImage: '/assets/services/cloud-compliance-left-image.png',
    rightImage: '/assets/services/cloud-compliance-right-image.png'
  },
  content: {
    sections: [
      {
        title: 'What We Offer',
        type: 'info-cards',
        info_cards_content: [
          {
            id:'card1',
            number:'01',
            title: 'Infrastructure Hardening & Access Control',
            description: 'Translate compliance controls into secure infrastructure deployments using Terraform, AWS IAM, and Kubernetes RBAC.',
            features: ['Encryption by default', 'Robust logging', 'Multi-cloud policies'],
            bgColor: 'white',
            image: '/assets/allServices/Access-Control.png'
          },
          {
            id: 'card2',
            number: '02',
            title: 'Internal Processes & Evidence Collection',
            description: 'DevSecOps-first approach that automates evidence generation through CI/CD logs and Git histories.',
            features: ['Automated evidence', 'SDLC policies', 'Change approval workflows'],
            bgColor: 'white',
            image: '/assets/allServices/Internal-Processes.png'
          },
          {
            id:'card3',
            number:'03',
            title: 'Documentation & Policy Automation',
            description: 'Version-controlled policies in Git with enforcement integrated directly into engineering workflows.',
            features: ['Git-based policies', 'Training schedules', 'Auto-review cadences'],
            bgColor: 'white',
            image: '/assets/allServices/Documentation.png'
          }
        ]
      }
    ]
  },
  faq: {
    title: 'Cloud Compliance & Security Services FAQ',
    faqs: [
      {
        question: 'Which compliance frameworks do you support?',
        answer: 'We prepare organizations for SOC 2, HIPAA, GDPR, ISO 27001, and PCI DSS audits. Our engineers map technical controls directly to framework requirements, so evidence collection is continuous rather than a scramble before each audit.'
      },
      {
        question: 'How long does it take to become audit-ready?',
        answer: 'Most startups reach SOC 2 audit-readiness in 8–12 weeks. Timelines vary with your current security posture and the framework in scope—we begin with a gap assessment and deliver a realistic, prioritized roadmap in the first week.'
      },
      {
        question: 'Do we need expensive compliance SaaS platforms?',
        answer: 'No. We build compliance automation directly into your infrastructure with policy-as-code, evidence pipelines, and monitoring you own—eliminating recurring per-seat SaaS costs while keeping you continuously audit-ready.'
      }
    ]
  }
};

// CI/CD Services
const cicdService: CompleteServiceData = {
  hero: {
    icon: '/assets/services/cicd.png',
    category: 'DevOps',
    title: 'CI/CD Services',
    subtitle: 'Advanced Continuous Integration & Deployment',
    description: 'Robust, end-to-end automation framework that accelerates software delivery, data integration, and ML model deployment.',
    highlights: ['Multi-Tool Expertise', 'Security Integration', 'Automated Testing', 'GitOps Integration'],
    leftImage: '/assets/services/ci-cd-left-image.png',
    rightImage: '/assets/services/ci-cd-right-image.png'
  },
  content: {
    sections: [
      {
        title: 'Expertise in Major CI/CD Tools',
        type: 'tools',
        tools: [
          {
            subheading: "Primary Platforms",
            text: "We help implement and optimize your CI/CD pipeline with the most popular DevOps platforms, ensuring efficiency and scalability.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "PrimaryPlatforms.png"
          },
          {
            subheading: "Cloud-Native",
            text: "Adopt and scale cloud-native CI/CD systems that integrate tightly with your preferred cloud providers and Kubernetes environments.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "Cloud-Native.png"
          },
          {
            subheading: "GitOps Controllers",
            text: "Enable automated, declarative, and version-controlled deployments using the latest GitOps controllers for Kubernetes-native delivery.",
            points: [
              "Expert consultation and implementation",
              "Industry best practices",
              "Ongoing support and optimization"
            ],
            image: "GitOpsControllers.png"
          }
        ]
      },
      {
        title: 'Why ReventLabs for CI/CD Services?',
        background:'white',
        type: 'grid-cards',
        grid_cards_content: [
          {
            image: 'DomainExpertise.png',
            title: 'Domain Expertise in Code, Data & Model Pipelines',
            description: 'Built CI/CD pipelines for application code, data artifacts, and ML models with automated validation.',
            features: ['Data provenance checks', 'Model validation', 'Multi-artifact support']
          },
          {
            image: 'GitOps.png',
            title: 'GitOps & Progressive Delivery Mastery',
            description: 'GitOps-centric workflows with canary releases, A/B tests, and blue/green deployments.',
            features: ['Progressive delivery', 'Automated rollbacks', 'Risk reduction']
          }
        ]
      }
    ]
  },
  faq: {
    title: 'CI/CD Services FAQ',
    faqs: [
      {
        question: 'How long does it take to modernize our CI/CD pipelines?',
        answer: 'Most teams have a modern, fully automated pipeline in production within 2–4 weeks. We map your current delivery workflow first, then introduce automated testing, security scanning, and progressive delivery incrementally—without freezing releases.'
      },
      {
        question: 'Which CI/CD tools do you work with?',
        answer: 'We build on GitHub Actions, GitLab CI, Jenkins, Argo CD, and Tekton, among others. Rather than forcing a migration, we optimize what fits your stack and only recommend replatforming when it clearly pays off.'
      },
      {
        question: 'Can you add deployment safety without slowing us down?',
        answer: 'Yes. Canary releases, blue/green deployments, and automated rollbacks let you ship more often with less risk. Our clients typically increase deployment frequency while reducing change failure rates.'
      }
    ]
  }
};

// Backup & Disaster Recovery Services
const backupDrService: CompleteServiceData = {
  hero: {
    icon: '/assets/services/Backup.png',
    category: 'Data Protection',
    title: 'Backup & Disaster Recovery Services',
    subtitle: 'Always Ready, Never Caught Off-Guard',
    description: 'Cloud-native, Kubernetes-aware backup and disaster recovery strategies that align with business continuity goals.',
    highlights: ['Kubernetes-Native', 'Multi-Cloud DR', 'Compliance-Ready', 'Automated Testing'],
    leftImage: '/assets/services/backup-disaster-left-image.png',
    rightImage: '/assets/services/backup-disaster-right-image.png'
  },
  content: {
    sections: [
      {
        title: 'What We Offer',
        type: 'info-cards',
        info_cards_content: [
          {
            id:'card1',
            number: '01',
            title: 'Kubernetes-Aware Backup Strategies',
            description: 'Intelligent, incremental backups using Velero, Stash, and Kasten K10 for stateless and stateful apps.',
            features: ['Persistent volumes', 'CRDs and secrets', 'Full cluster states'],
            bgColor: 'white',
            image: '/assets/allServices/Kubernetes-Aware.png'
          },
          {
            id:'card2',
            number:'02',
            title: 'Cloud-Native Disaster Recovery',
            description: 'Secure, incremental database backups across accounts and regions, fully encrypted.',
            features: ['Multi-region replication', 'DNS failover', 'Minutes RTO/RPO'],
            bgColor: 'white',
            image: '/assets/allServices/Cloud-Native-2.png'
          },
          {
            id:'card3',
            number:'03',
            title: 'Application-Centric DR',
            description: 'DR flows tailored to business-critical services with GitOps tooling for instant redeployment.',
            features: ['GitOps integration', 'DNS switching', 'Config drift prevention'],
            bgColor: 'white',
            image: '/assets/allServices/Application-Centric.png'
          }
        ]
      }
    ]
  },
  faq: {
    title: 'Backup & Disaster Recovery Services FAQ',
    faqs: [
      {
        question: 'How quickly can we recover from a failure or outage?',
        answer: 'We design recovery around your business requirements, defining RTO and RPO per service. With GitOps-driven redeployment and automated failover, critical workloads can often be restored in minutes rather than hours.'
      },
      {
        question: 'Do you test the disaster recovery plans you build?',
        answer: 'Yes—an untested DR plan is a liability. We run scheduled recovery drills and simulated outages, validate restore integrity, and refine runbooks so your team is confident before a real incident occurs.'
      },
      {
        question: 'Can your backup architecture support our compliance requirements?',
        answer: 'Absolutely. We align backup retention, encryption, and access controls with SOC 2, ISO 27001, HIPAA, and PCI DSS requirements, producing the audit evidence you need automatically.'
      }
    ]
  }
};

// Penetration Testing
const penetrationTestingService: CompleteServiceData = {
  hero: {
    icon: '/assets/services/Security.png',
    category: 'Security Testing',
    title: 'Penetration Testing',
    subtitle: 'Advanced Security Testing with Industry-Leading Tools & Methodologies',
    description: 'Comprehensive security assessments using cutting-edge penetration testing tools and proven methodologies. We identify vulnerabilities before attackers do, providing actionable remediation strategies that protect your digital assets and ensure compliance with industry standards.',
    highlights: ['OWASP Top 10 Compliance', 'CVSS 3.1 Scoring', 'Multi-Platform Testing', 'Detailed Remediation Reports', '24/7 Security Monitoring', 'Compliance Validation'],
    leftImage: '/assets/services/vulnerability-left-image.png',
    rightImage: '/assets/services/vulnerability-right-image.png'
  },
  content: {
    sections: [
      {
        title: 'Comprehensive Security Assessment Types',
        type: 'info-cards',
        info_cards_content: [
          {
            id: 'card1',
            number:'01',
            title: 'Black Box Penetration Testing',
            description: 'Simulate real-world external attacks with zero prior knowledge of your systems. Our ethical hackers approach your infrastructure as malicious actors would, using only publicly available information.',
            features: ['External attacker simulation', 'Real-world attack scenarios', 'Zero system disruption', 'Public reconnaissance techniques', 'Social engineering assessment'],
            bgColor: 'white',
            image: '/assets/allServices/Black-Box.png'
          },
          {
            id:'card2',
            number:'02',
            title: 'White Box Security Assessment',
            description: 'Comprehensive internal security testing with full system access and documentation. We analyze source code, architecture, and internal processes to identify vulnerabilities that external attackers might miss.',
            features: ['Source code analysis', 'Architecture review', 'Internal process testing', 'Privilege escalation testing', 'Database security assessment'],
            bgColor: 'white',
            image: '/assets/allServices/White-Box.png'
          },
          {
            id:'card3',
            number:'03',
            title: 'Gray Box Testing',
            description: 'Balanced approach combining external and internal perspectives. Limited system knowledge allows for realistic testing scenarios while maintaining comprehensive coverage of potential attack vectors.',
            features: ['Hybrid testing approach', 'Limited privilege testing', 'Realistic attack simulation', 'Comprehensive coverage', 'Business logic testing'],
            bgColor: 'white',
            image: '/assets/allServices/White-Box.png'
          },
          {
            id:'card4',
            number:'04',
            title: 'Red Team Operations',
            description: 'Advanced persistent threat simulation using sophisticated attack techniques. Our red team mimics nation-state actors and advanced persistent threats to test your organization\'s detection and response capabilities.',
            features: ['APT simulation', 'Multi-vector attacks', 'Social engineering campaigns', 'Physical security testing', 'Blue team engagement'],
            bgColor: 'white',
            image: '/assets/allServices/Red-Team.png'
          }
        ]
      },
      {
        title: 'Our Penetration Testing Services',
        type: 'info-cards',
        info_cards_content: [
          {
            id: 'card1',
            number: '01',
            title: 'Web Application Penetration Testing (WAPT)',
            description:
              'Deep-dive security testing of web applications covering OWASP Top 10 and beyond — authentication, session management, injection, business logic, and access controls.',
            features: [
              'OWASP Top 10 & business logic flaws',
              'Authentication & session hijacking',
              'Broken access control & privilege escalation',
              'Input validation & injection testing',
              'API endpoint security within web apps'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Black-Box.png'
          },
          {
            id: 'card2',
            number: '02',
            title: 'External / Internal Network Penetration Testing (VAPT)',
            description:
              'Simulate real-world attackers targeting your external perimeter and internal network — identifying open services, misconfigurations, and lateral movement paths.',
            features: [
              'External perimeter & DMZ assessment',
              'Internal network segmentation review',
              'Lateral movement & privilege escalation',
              'Vulnerability chaining scenarios',
              'Post-exploitation impact analysis'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Disaster-Recovery.png'
          },
          {
            id: 'card3',
            number: '03',
            title: 'API Security Testing',
            description:
              'Comprehensive testing of REST, GraphQL, and gRPC APIs — assessing broken authorization, mass assignment, injection, and improper rate limiting per OWASP API Top 10.',
            features: [
              'OWASP API Top 10 coverage',
              'BOLA / BFLA authorization testing',
              'Mass assignment vulnerability checks',
              'Rate limiting & throttling bypass',
              'Token, JWT & OAuth security review'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Advanced-Alerting.png'
          },
          {
            id: 'card4',
            number: '04',
            title: 'Mobile App Penetration Testing (MAPT)',
            description:
              'Security assessment for iOS and Android apps following OWASP MASVS/MSTG — covering data storage, cryptography, IPC, reverse engineering, and client-server trust.',
            features: [
              'OWASP MASVS / MSTG framework',
              'Static (SAST) & dynamic (DAST) analysis',
              'Insecure data storage & cryptography review',
              'IPC & deep-link attack surface testing',
              'Certificate pinning bypass attempts'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Monitoring-Architecture.png'
          },
          {
            id: 'card5',
            number: '05',
            title: 'Thick Client Penetration Testing',
            description:
              'Security testing of desktop applications — binary analysis, memory inspection, local storage review, IPC, and network traffic interception across two-tier and three-tier architectures.',
            features: [
              'Binary & reverse engineering analysis',
              'Memory & local storage inspection',
              'IPC and named pipe abuse testing',
              'Network traffic manipulation',
              'DLL hijacking & privilege abuse'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Infrastructure-into-IaC.png'
          },
          {
            id: 'card6',
            number: '06',
            title: 'AI / LLM Penetration Testing (AIPT)',
            description:
              'Specialised testing for AI-powered applications and LLM integrations — covering prompt injection, model extraction, data leakage, and guardrail bypass per OWASP LLM Top 10.',
            features: [
              'Prompt injection & jailbreak testing',
              'Model inversion & extraction attacks',
              'OWASP LLM Top 10 alignment',
              'RAG pipeline & plugin security review',
              'Data leakage & PII exposure testing'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Federated-Metric.png'
          },
          {
            id: 'card7',
            number: '07',
            title: 'Source Code Review',
            description:
              'Manual and tool-assisted review of application source code to identify security flaws at root cause — logic errors, insecure dependencies, hardcoded secrets, and injection risks.',
            features: [
              'SAST tool-assisted + manual review',
              'Hardcoded secrets & credential detection',
              'Insecure dependency & supply chain analysis',
              'Business logic vulnerability detection',
              'Secure coding recommendations'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Continuous-Drift.png'
          }
        ]
      },
      {
        title: 'Industry Standards & Compliance Frameworks',
        background:'black',
        type: 'list',
        items: [
          'OWASP Top 10 2021 - Latest web application security risks and mitigation strategies',
          'NIST Cybersecurity Framework 2.0 - Comprehensive security controls and risk management',
          'PTES (Penetration Testing Execution Standard) - Structured methodology for all testing phases',
          'CVSS 3.1 - Common Vulnerability Scoring System for accurate risk assessment',
          'ISO 27001/27002 - International information security management standards',
          'PCI DSS - Payment card industry security requirements for financial services',
          'SOC 2 Type II - Service organization controls for security, availability, and confidentiality',
          'HIPAA - Healthcare industry security and privacy requirements',
          'GDPR - European data protection and privacy regulations',
          'CIS Controls - Critical security controls for effective cyber defense'
        ]
      },
      {
        title: 'Security Assessment Engagement Roadmap',
        background:'white',
        type: 'process',
        processSteps: [
          {
            step: 1,
            title: 'Define Scope',
            description: 'Establish the assessment\'s boundaries, goals, and rules of engagement.'
          },
          {
            step: 2,
            title: 'Information Gathering',
            description: 'Collect data through reconnaissance techniques to understand the target environment.'
          },
          {
            step: 3,
            title: 'Threat Modeling',
            description: 'Identify potential threats and attack vectors relevant to the target systems.'
          },
          {
            step: 4,
            title: 'Vulnerability Identification',
            description: 'Utilize automated tools and manual methods to discover security weaknesses.'
          },
          {
            step: 5,
            title: 'Vulnerability Analysis',
            description: 'Assess the discovered vulnerabilities to determine their potential impact and exploitability.'
          },
          {
            step: 6,
            title: 'Exploitation Planning',
            description: 'Develop strategies to exploit identified vulnerabilities in a controlled manner.'
          },
          {
            step: 7,
            title: 'Exploitation',
            description: 'Attempt to exploit vulnerabilities to validate their existence and potential impact.'
          },
          {
            step: 8,
            title: 'Post-Exploitation',
            description: 'Determine the extent of access gained and the potential for lateral movement within the network.'
          },
          {
            step: 9,
            title: 'Reporting',
            description: 'Document findings, including vulnerabilities discovered, exploitation results, and recommended remediation steps.'
          },
          {
            step: 10,
            title: 'Remediation and Retesting',
            description: 'Collaborate with stakeholders to address identified issues and verify the effectiveness of implemented fixes.'
          }
        ]
      },
      {
        title: 'Our Security Testing Tech Stack',
        background: 'white',
        type: 'tools',
        tools: [
          {
            subheading: "Vulnerability Scanners",
            text: "Comprehensive vulnerability scanning tools for automated discovery of security weaknesses across web applications, networks, and infrastructure.",
            points: [
              "Automated vulnerability detection",
              "Comprehensive coverage scanning",
              "Real-time threat intelligence"
            ],
            image: "/assets/allServices/Advanced-Alerting.png"
          },
          {
            subheading: "Penetration Testing Tools",
            text: "Professional-grade penetration testing frameworks and tools for manual security testing and exploitation validation.",
            points: [
              "Manual testing capabilities",
              "Exploit validation",
              "Custom attack scenarios"
            ],
            image: "/assets/allServices/Security.png"
          },
          {
            subheading: "Web Application Security",
            text: "Specialized tools for testing web applications, APIs, and cloud-native services for OWASP Top 10 vulnerabilities.",
            points: [
              "OWASP compliance testing",
              "API security assessment",
              "Cloud-native security"
            ],
            image: "/assets/allServices/Cloud-Native.png"
          },
          {
            subheading: "Network Security Tools",
            text: "Network penetration testing tools for infrastructure security assessment and network vulnerability discovery.",
            points: [
              "Network vulnerability scanning",
              "Infrastructure assessment",
              "Wireless security testing"
            ],
            image: "/assets/allServices/Infrastructure-into-IaC.png"
          },
          {
            subheading: "Reporting & Documentation",
            text: "Professional reporting tools and documentation frameworks for comprehensive security assessment reports.",
            points: [
              "Detailed vulnerability reports",
              "CVSS scoring integration",
              "Remediation guidance"
            ],
            image: "/assets/allServices/Monitoring-Architecture.png"
          },
          {
            subheading: "Compliance & Standards",
            text: "Tools and frameworks aligned with industry standards including OWASP, NIST, PTES, and CVSS for standardized security testing.",
            points: [
              "Industry standard compliance",
              "Standardized scoring",
              "Regulatory alignment"
            ],
            image: "/assets/allServices/Automation-First.png"
          }
        ]
      },
      {
        title: 'Comprehensive Deliverables & Reporting',
        background: 'white',
        type: 'info-cards',
        info_cards_content: [
          {
            id: 'deliverables1',
            number: '01',
            title: 'Executive Summary Report',
            description: 'High-level overview of security posture, critical findings, and strategic recommendations for leadership and stakeholders.',
            features: [
              'Risk assessment overview',
              'Critical vulnerability summary',
              'Business impact analysis',
              'Strategic recommendations',
              'Compliance status overview'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Monitoring-Architecture.png'
          },
          {
            id: 'deliverables2',
            number: '02',
            title: 'Technical Vulnerability Report',
            description: 'Detailed technical findings with proof-of-concept exploits, CVSS scoring, and step-by-step remediation guidance.',
            features: [
              'Detailed vulnerability descriptions',
              'CVSS 3.1 scoring for each finding',
              'Proof-of-concept exploit code',
              'Step-by-step remediation instructions',
              'Risk prioritization matrix'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Advanced-Alerting.png'
          },
          {
            id: 'deliverables3',
            number: '03',
            title: 'Compliance Assessment Report',
            description: 'Comprehensive compliance evaluation against industry standards and regulatory requirements with gap analysis.',
            features: [
              'OWASP Top 10 compliance status',
              'NIST framework alignment',
              'Industry-specific compliance (PCI, HIPAA, etc.)',
              'Gap analysis and recommendations',
              'Compliance roadmap'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Automation-First.png'
          },
          {
            id: 'deliverables4',
            number: '04',
            title: 'Remediation Support & Retesting',
            description: 'Ongoing support for vulnerability remediation with verification testing and security validation.',
            features: [
              'Remediation consultation',
              'Verification testing',
              'Security validation',
              'Follow-up assessments',
              'Security training materials'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Security.png'
          }
        ]
      },
      {
        title: 'Why Choose Our Security Testing Services',
        background: 'black',
        type: 'list',
        items: [
          'Certified Security Professionals - Our team holds industry certifications including CISSP, CEH, OSCP, and CISM',
          'Proven Methodology - Following PTES, OWASP, and NIST frameworks for comprehensive security assessments',
          'Advanced Tool Arsenal - Using cutting-edge commercial and open-source tools for thorough vulnerability discovery',
          'Detailed Reporting - Comprehensive reports with actionable remediation guidance and business impact analysis',
          'Compliance Expertise - Deep knowledge of industry standards including PCI DSS, HIPAA, SOC 2, and GDPR',
          '24/7 Support - Round-the-clock support during and after security assessments',
          'Cost-Effective Solutions - Competitive pricing with flexible engagement models to fit your budget',
          'Confidentiality Guaranteed - Strict NDAs and secure handling of all sensitive information',
          'Rapid Turnaround - Quick assessment completion without compromising thoroughness',
          'Ongoing Partnership - Long-term security partnership with regular assessments and monitoring'
        ]
      }
    ]
  },
  faq: {
    title: 'Penetration Testing FAQ',
    faqs: [
      {
        question: 'What types of security assessments do you offer?',
        answer: 'We offer comprehensive security testing including black box, gray box, and white box assessments, red team exercises, cloud security testing, mobile application security, and specialized compliance assessments. All testing follows OWASP, NIST, and PTES methodologies with industry-standard tools and techniques.'
      },
      {
        question: 'How long does a typical penetration test take?',
        answer: 'Assessment duration varies based on scope and complexity. Web application testing typically takes 1-2 weeks, infrastructure assessments require 2-4 weeks, and comprehensive red team exercises may take 4-8 weeks. We provide detailed timelines during the scoping phase and maintain regular communication throughout the engagement.'
      },
      {
        question: 'What tools and methodologies do you use?',
        answer: 'We use industry-leading tools including Burp Suite, OWASP ZAP, Nmap, Nessus, Metasploit, and specialized cloud security tools. Our methodology follows PTES (Penetration Testing Execution Standard), OWASP guidelines, and NIST frameworks, ensuring comprehensive and standardized assessments.'
      },
      {
        question: 'Do you provide remediation guidance for identified vulnerabilities?',
        answer: 'Yes, our comprehensive reports include detailed remediation steps, CVSS 3.1 scoring, proof-of-concept exploits, and prioritized recommendations. We also offer remediation consultation, verification testing, and ongoing support to help your team implement security fixes effectively.'
      },
      {
        question: 'Are your security assessments compliant with industry standards?',
        answer: 'Absolutely. We follow OWASP Top 10 2021, NIST Cybersecurity Framework 2.0, PTES methodology, and provide CVSS 3.1 scoring. Our assessments are designed to meet SOC 2, PCI DSS, HIPAA, GDPR, ISO 27001, and other regulatory requirements with detailed compliance reporting.'
      },
      {
        question: 'What certifications do your security professionals hold?',
        answer: 'Our team holds industry-leading certifications including CISSP, CEH, OSCP, CISM, CISA, and specialized cloud security certifications. We maintain continuous education and stay current with the latest security trends, tools, and attack techniques.'
      },
      {
        question: 'How do you ensure confidentiality during testing?',
        answer: 'We sign strict NDAs and follow secure handling procedures for all sensitive information. Our testing is conducted in isolated environments when possible, and all data is encrypted in transit and at rest. We provide detailed security protocols and maintain complete confidentiality throughout the engagement.'
      },
      {
        question: 'Do you offer retesting after remediation?',
        answer: 'Yes, we provide comprehensive retesting services to verify that vulnerabilities have been properly remediated. This includes verification testing, security validation, and follow-up assessments to ensure your security posture has improved. We also offer ongoing security monitoring and regular assessment schedules.'
      },
      {
        question: 'What makes your penetration testing different from automated scans?',
        answer: 'While we use automated tools for initial discovery, our approach combines automated scanning with manual testing, custom exploit development, and business logic analysis. Our certified professionals provide human intelligence that automated tools cannot, identifying complex vulnerabilities and providing context-aware risk assessments.'
      },
      {
        question: 'Do you provide training for our security team?',
        answer: 'Yes, we offer comprehensive security training including secure coding practices, vulnerability management, incident response, and security awareness training. We also provide customized training materials and workshops tailored to your specific technology stack and security requirements.'
      }
    ]
  }
};

// ADVANCED RED TEAM & ADVERSARY SIMULATION
const redTeamService: CompleteServiceData = {
  hero: {
    icon: '/assets/services/Security.png',
    category: 'Adversary Simulation',
    title: 'Advanced Red Team & Adversary Simulation',
    subtitle: 'Test Your Defences Against Real-World Adversaries',
    description:
      'Go beyond standard pentesting with full-scope red team engagements and assumed breach assessments that expose gaps in detection, response, and containment capabilities.',
    highlights: [
      'APT-Style Threat Simulation',
      'Multi-Vector Attack Campaigns',
      'MITRE ATT&CK Aligned',
      'Detection & Response Gap Analysis'
    ],
    ctaButtons: {
      primary: { text: 'Start a Red Team Engagement', link: '/contact' },
      secondary: { text: 'Learn More', link: '/case-studies' }
    },
    leftImage: '/assets/services/RedTeamTesting.png',
    rightImage: '/assets/services/kubernetes-left-image.png'
  },
  content: {
    sections: [
      // ── Section 1: Service Offerings ──
      {
        title: 'Red Team Service Offerings',
        type: 'info-cards',
        info_cards_content: [
          {
            id: 'card1',
            number: '01',
            title: 'Full-Scope Red Team Engagements',
            description:
              'A realistic, goal-oriented adversary simulation spanning digital, physical, and human attack surfaces. Our red team operates covertly over weeks, mimicking APT actors to test your organisation\'s ability to detect, respond, and contain a sophisticated breach.',
            features: [
              'Multi-vector campaigns: phishing, vishing, physical, digital',
              'APT-style covert operations with custom tooling',
              'MITRE ATT&CK framework aligned TTPs',
              'C2 infrastructure & evasion techniques',
              'Detection & response gap identification',
              'Executive debrief & purple team workshop'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Red-Team.png'
          },
          {
            id: 'card2',
            number: '02',
            title: 'Assumed Breach Assessments',
            description:
              'Starting from a post-compromise position — simulating an attacker who has already bypassed perimeter defences — we assess lateral movement, privilege escalation, and data exfiltration paths before detection.',
            features: [
              'Lateral movement & network traversal',
              'Active Directory & identity attack paths',
              'Credential harvesting, Pass-the-Hash / Pass-the-Ticket',
              'Domain dominance & persistence simulation',
              'Data exfiltration scenario testing',
              'SOC detection & response timeline measurement'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Disaster-Recovery.png'
          }
        ]
      },

      // ── Section 2: Why Red Team ──
      {
        title: 'Why Red Teaming Goes Further Than Pentesting',
        type: 'animated-info-cards',
        animated_info_cards_content: [
          {
            id: 'card1',
            number: '01',
            title: 'Test People, Process & Technology Together',
            description:
              'Red team engagements stress-test your entire security programme. We evaluate how well your SOC, IR team, and employees detect and respond under realistic attack conditions.',
            features: [
              'SOC detection capability measurement',
              'Incident response process validation',
              'Security awareness & phishing resilience'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Proven-Enterprise.png'
          },
          {
            id: 'card2',
            number: '02',
            title: 'Identify Blind Spots in Detection Coverage',
            description:
              'By operating covertly with real attacker TTPs, we reveal SIEM rule blind spots, detection gaps, and alert fatigue issues that standard tests can never surface.',
            features: [
              'SIEM & EDR evasion testing',
              'Alert fatigue & noise analysis',
              'Detection rule gap identification'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Anycast-Ingress.png'
          },
          {
            id: 'card3',
            number: '03',
            title: 'Measure Real Response Capability',
            description:
              'We measure actual MTTD and MTTR under a realistic breach scenario — giving leadership a data-driven view of security programme maturity.',
            features: [
              'MTTD & MTTR measurement',
              'IR playbook effectiveness testing',
              'Security programme maturity scoring'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Time-to-Value.png'
          },
          {
            id: 'card4',
            number: '04',
            title: 'Purple Team Collaboration',
            description:
              'Every engagement concludes with a structured purple team workshop — red and blue teams working together to tune detection rules, improve playbooks, and transfer attacker knowledge.',
            features: [
              'Joint red & blue team debrief',
              'Detection rule tuning sessions',
              'Playbook improvement workshops'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Kubernetes-Specific.png'
          }
        ]
      },

      // ── Section 3: What We Simulate ──
      {
        title: 'What Our Red Team Simulates',
        background: 'blue',
        type: 'benefits-card',
        benefits_card_content: [
          {
            image: 'Migration&Modernization.png',
            title: 'Phishing & Social Engineering',
            description: 'Spear-phishing, vishing, smishing, and pretexting campaigns targeting employees, executives, and third-party vendors.',
            features: [
              'Spear-phishing campaigns',
              'Executive (whaling) targeting',
              'Vendor & supply chain pretexting'
            ]
          },
          {
            image: 'CloudGovernance.png',
            title: 'Initial Access & Exploitation',
            description: 'Exploitation of internet-facing assets, credential stuffing, and watering-hole attacks to gain initial foothold.',
            features: [
              'External asset exploitation',
              'Credential stuffing attacks',
              'Watering-hole simulation'
            ]
          },
          {
            image: 'MigrationCostAssessment.png',
            title: 'Lateral Movement & Persistence',
            description: 'Active Directory attacks, Pass-the-Hash, Kerberoasting, Golden Ticket, and persistence via scheduled tasks, registry, and WMI subscriptions.',
            features: [
              'Active Directory attack paths',
              'Kerberoasting & Golden Ticket',
              'Persistence mechanism testing'
            ]
          },
          {
            image: 'SeamlessMulti-Cloud.png',
            title: 'Data Exfiltration Simulation',
            description: 'Controlled exfiltration via DNS tunnelling, HTTPS covert channels, and cloud storage abuse to test DLP and egress controls.',
            features: [
              'DNS tunnelling simulation',
              'HTTPS covert channel exfiltration',
              'DLP & egress control testing'
            ]
          },
          {
            image: 'Full-StackSRE.png',
            title: 'Physical Security Testing',
            description: 'On-site testing including tailgating, badge cloning, office intrusion, and rogue device planting to test physical access controls.',
            features: [
              'Tailgating & badge cloning',
              'Office intrusion scenarios',
              'Rogue device deployment'
            ]
          }
        ]
      },

      // ── Section 4: Engagement Roadmap ──
      {
        title: 'Red Team Engagement Roadmap',
        background: 'white',
        type: 'process',
        processSteps: [
          {
            step: 1,
            title: 'Objective Setting & Rules of Engagement',
            description: 'Define the engagement objective (e.g. reach domain admin, exfiltrate crown jewel data). Agree rules of engagement, out-of-scope systems, and escalation contacts.'
          },
          {
            step: 2,
            title: 'Intelligence Gathering & Target Profiling',
            description: 'OSINT collection on the organisation, employees, technology stack, and exposed assets. Build attacker profile and identify highest-probability initial access vectors.'
          },
          {
            step: 3,
            title: 'Initial Access',
            description: 'Execute planned initial access scenarios — phishing, external asset exploitation, or physical intrusion — to gain a foothold in the target environment.'
          },
          {
            step: 4,
            title: 'Covert Operations & Lateral Movement',
            description: 'Operate stealthily — escalating privileges, moving laterally, compromising identity infrastructure, and working toward the defined objective.'
          },
          {
            step: 5,
            title: 'Objective Achievement & Impact Validation',
            description: 'Reach the defined objective and document the full attack path with timestamps, evidence, and detection events.'
          },
          {
            step: 6,
            title: 'Reporting & Executive Debrief',
            description: 'Deliver a full attack narrative, MITRE ATT&CK mapping, detection timeline, and prioritised recommendations. Present to leadership and the security team.'
          },
          {
            step: 7,
            title: 'Purple Team Workshop',
            description: 'Joint session with your blue team to replay attack techniques, tune detection rules, improve IR playbooks, and transfer attacker knowledge to defenders.'
          }
        ]
      },

      // ── Section 5: Stats ──
      {
        title: 'What Our Engagements Reveal',
        type: 'rich-text',
        rich_text_content: [
          { percent: 78, barColor: '#EF4444', topic: 'Engagements Where Initial Access Was Achieved via Phishing' },
          { percent: 65, barColor: '#F59E0B', topic: 'Engagements Where Domain Admin Was Reached Undetected' },
          { percent: 90, barColor: '#8B5CF6', topic: 'Improvement in Detection Coverage After Purple Team Workshop' }
        ]
      },

      // ── Section 6: What You Get ──
      {
        title: 'What You Get',
        background: 'blue',
        type: 'features',
        features_content: [
          { text: 'Full attack narrative with MITRE ATT&CK TTP mapping' },
          { text: 'Detection gap analysis with MTTD & MTTR measurement' },
          { text: 'Prioritised remediation roadmap for people, process, and technology' },
          { text: 'Executive summary for leadership and board-level reporting' },
          { text: 'Purple team workshop to tune detection and harden IR playbooks' },
          { text: 'Assumed breach scenario to stress-test internal controls and SOC' }
        ]
      }
    ]
  },
  faq: {
    title: 'Red Team & Adversary Simulation FAQ',
    faqs: [
      {
        question: 'How is a red team engagement different from a penetration test?',
        answer: 'A penetration test is time-boxed and focused on finding as many vulnerabilities as possible. A red team engagement is objective-driven and covert — we simulate a real adversary pursuing a specific goal to test detection and response capabilities, not just defences.'
      },
      {
        question: 'Will our security team know the red team engagement is happening?',
        answer: 'Typically no — only a small group of stakeholders (the "white cell") are aware. This ensures the blue team and SOC respond authentically, giving a true measure of detection and response capability.'
      },
      {
        question: 'How long does a full-scope red team engagement take?',
        answer: 'Most full-scope engagements run 4–8 weeks depending on objective complexity. Assumed breach assessments are typically shorter at 2–4 weeks.'
      },
      {
        question: 'What is a purple team workshop?',
        answer: 'A collaborative session where our red team replays attack techniques alongside your blue team to tune detection rules, improve alert thresholds, and strengthen IR playbooks based on real attacker behaviour observed during the engagement.'
      }
    ]
  }
};

// CONFIGURATION SECURITY REVIEW
const configSecurityReviewService: CompleteServiceData = {
  hero: {
    icon: '/assets/services/Cloud-Compliance.png',
    category: 'Security Review',
    title: 'Configuration Security Review',
    subtitle: 'Eliminate Misconfigurations Before They Become Breaches',
    description:
      'Expert-led security reviews of your network device configurations — firewalls, routers, switches, and VPNs — closing the misconfiguration gaps attackers exploit most.',
    highlights: [
      'Firewall Rule Optimisation',
      'Network Device Hardening',
      'VPN Security Assessment',
      'CIS Benchmark Aligned'
    ],
    ctaButtons: {
      primary: { text: 'Request a Config Review', link: '/contact' },
      secondary: { text: 'Learn More', link: '/case-studies' }
    },
    leftImage: '/assets/services/cloud-compliance-left-image.png',
    rightImage: '/assets/services/cloud-compliance-right-image.png'
  },
  content: {
    sections: [
      // ── Section 1: Service Offerings ──
      {
        title: 'Configuration Security Review Services',
        type: 'info-cards',
        info_cards_content: [
          {
            id: 'card1',
            number: '01',
            title: 'Firewall Configuration Review',
            description:
              'Comprehensive analysis of firewall rule sets to identify overly permissive policies, rule shadowing, unused rules, implicit denies, and compliance gaps — reducing attack surface without impacting legitimate traffic.',
            features: [
              'Rule-set optimisation & redundancy elimination',
              'Ingress & egress policy validation',
              'Implicit deny & default rule review',
              'Zone-based segmentation assessment',
              'Compliance alignment (PCI DSS, NIST, CIS)'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Access-Control.png'
          },
          {
            id: 'card2',
            number: '02',
            title: 'Router, Switch & Network Device Configuration Review',
            description:
              'Security review of routers, switches, and network appliances for insecure defaults, weak authentication, unencrypted management planes, misconfigured VLANs, and unnecessary services.',
            features: [
              'Default credential & insecure protocol identification',
              'VLAN segmentation & trunking review',
              'Management plane hardening (SSH, SNMPv3, ACLs)',
              'Routing protocol security (BGP, OSPF authentication)',
              'CIS Benchmark alignment for network devices'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Infrastructure-into-IaC.png'
          },
          {
            id: 'card3',
            number: '03',
            title: 'VPN Configuration Security Review',
            description:
              'Evaluation of VPN gateways and client configurations for weak cipher suites, outdated protocols, split-tunnelling risks, certificate management issues, and authentication weaknesses.',
            features: [
              'Cipher suite & protocol version analysis (IKEv2, TLS 1.3)',
              'Split-tunnel risk & DNS leak assessment',
              'MFA & certificate lifecycle review',
              'Client configuration hardening',
              'Zero Trust Network Access (ZTNA) readiness'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Continuous-Drift.png'
          }
        ]
      },

      // ── Section 2: What We Assess ──
      {
        title: 'What We Assess in Every Review',
        type: 'animated-info-cards',
        animated_info_cards_content: [
          {
            id: 'card1',
            number: '01',
            title: 'Authentication & Access Controls',
            description:
              'Review of all administrative access controls — weak passwords, default credentials, shared accounts, and unnecessary management plane exposure.',
            features: [
              'Default credential detection',
              'Shared account identification',
              'Management plane access restriction'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Proven-Enterprise.png'
          },
          {
            id: 'card2',
            number: '02',
            title: 'Encryption & Protocol Security',
            description:
              'Assessment of encryption standards in use — identifying deprecated protocols (SSLv3, TLS 1.0, MD5, DES) and recommending modern replacements.',
            features: [
              'Deprecated protocol identification',
              'Cipher suite strength analysis',
              'TLS 1.3 & IKEv2 upgrade recommendations'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Federated-Metric.png'
          },
          {
            id: 'card3',
            number: '03',
            title: 'Network Segmentation & Least Privilege',
            description:
              'Review of VLAN design, firewall zones, and inter-segment rules to ensure the principle of least privilege is enforced across the network fabric.',
            features: [
              'VLAN & zone boundary review',
              'Lateral movement path identification',
              'Least-privilege rule enforcement'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Disaster-Recovery.png'
          },
          {
            id: 'card4',
            number: '04',
            title: 'Logging, Monitoring & Audit Trails',
            description:
              'Verification that all devices generate sufficient logs, forward to a SIEM, and retain audit trails in line with compliance requirements.',
            features: [
              'Syslog & SIEM forwarding verification',
              'Log retention policy review',
              'Audit trail completeness assessment'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Monitoring-Architecture.png'
          }
        ]
      },

      // ── Section 3: Why Partner With Us ──
      {
        title: 'Why Partner with Us for Configuration Reviews?',
        background: 'blue',
        type: 'benefits-card',
        benefits_card_content: [
          {
            image: 'MigrationCostAssessment.png',
            title: 'Vendor-Agnostic Expertise',
            description: 'We review configurations across Cisco, Palo Alto, Fortinet, Juniper, Check Point, pfSense, and cloud-native firewalls (AWS SGs, Azure NSGs, GCP VPC Firewall).',
            features: [
              'Cisco, Palo Alto, Fortinet support',
              'Juniper, Check Point, pfSense',
              'Cloud-native firewall & NSG review'
            ]
          },
          {
            image: 'Security&Compliance.png',
            title: 'CIS Benchmark Aligned',
            description: 'All reviews are aligned to CIS Benchmarks and vendor-specific hardening guides to ensure industry-standard configuration baselines.',
            features: [
              'CIS Benchmark alignment',
              'Vendor hardening guide compliance',
              'Remediation priority scoring'
            ]
          },
          {
            image: 'SLOTracking.png',
            title: 'Risk-Based Finding Prioritisation',
            description: 'Findings are risk-scored and prioritised — giving your team a clear view of what to fix first based on exploitability and business impact.',
            features: [
              'CVSS-style risk scoring',
              'Business impact context',
              'Phased remediation roadmap'
            ]
          },
          {
            image: 'CloudGovernance.png',
            title: 'Compliance-Ready Outputs',
            description: 'Reports are structured to support PCI DSS, HIPAA, ISO 27001, NIST, and SOC 2 audit evidence requirements.',
            features: [
              'PCI DSS network control evidence',
              'ISO 27001 Annex A alignment',
              'Audit-ready documentation'
            ]
          }
        ]
      },

      // ── Section 4: Engagement Roadmap ──
      {
        title: 'Configuration Review Engagement Roadmap',
        background: 'white',
        type: 'process',
        processSteps: [
          {
            step: 1,
            title: 'Scoping & Asset Discovery',
            description: 'Define devices, platforms, and network segments in scope. Collect configuration files or perform live review via read-only access to network management systems.'
          },
          {
            step: 2,
            title: 'Automated Baseline Analysis',
            description: 'Run configurations through automated analysis tools (Nipper, CIS-CAT, Firemon) to baseline compliance against CIS Benchmarks and vendor hardening guides.'
          },
          {
            step: 3,
            title: 'Manual Expert Review',
            description: 'Our engineers perform manual review of rule sets, ACLs, routing policies, and VPN configurations to identify context-specific risks that automation cannot detect.'
          },
          {
            step: 4,
            title: 'Risk Scoring & Finding Prioritisation',
            description: 'Each finding is risk-scored based on exploitability, network exposure, and business impact — providing a clear, prioritised remediation order.'
          },
          {
            step: 5,
            title: 'Reporting & Remediation Guidance',
            description: 'Deliver an executive summary and detailed technical report with per-device findings, risk scores, and step-by-step remediation instructions.'
          },
          {
            step: 6,
            title: 'Remediation Support & Validation',
            description: 'Provide guidance throughout remediation and conduct a follow-up review to validate all critical and high findings have been effectively resolved.'
          }
        ]
      },

      // ── Section 5: Standards ──
      {
        title: 'Standards & Frameworks Applied',
        background: 'black',
        type: 'list',
        items: [
          'CIS Benchmarks — Vendor-specific hardening baselines for all major network devices',
          'NIST SP 800-41 — Guidelines on firewalls and firewall policy',
          'NIST SP 800-77 — Guide to IPsec VPNs',
          'PCI DSS Requirement 1 — Network access controls for cardholder data environments',
          'ISO 27001 Annex A.13 — Network security management controls',
          'DISA STIGs — Security Technical Implementation Guides for network infrastructure',
          'NSA/CISA Network Infrastructure Security Guidance',
          'Zero Trust Architecture principles (NIST SP 800-207)'
        ]
      },

      // ── Section 6: Stats ──
      {
        title: 'Misconfiguration Impact by the Numbers',
        type: 'rich-text',
        rich_text_content: [
          { percent: 70, barColor: '#EF4444', topic: 'Of Breaches Involve a Network Misconfiguration' },
          { percent: 85, barColor: '#F59E0B', topic: 'Reduction in Attack Surface After Firewall Rule Optimisation' },
          { percent: 60, barColor: '#10B981', topic: 'Of Firewall Rules Found to Be Overly Permissive or Unused' }
        ]
      },

      // ── Section 7: What You Get ──
      {
        title: 'What You Get',
        background: 'blue',
        type: 'features',
        features_content: [
          { text: 'Detailed per-device findings with risk scores and remediation steps' },
          { text: 'CIS Benchmark and vendor hardening guide compliance report' },
          { text: 'Firewall rule optimisation recommendations with impact analysis' },
          { text: 'VPN cipher suite and protocol upgrade roadmap' },
          { text: 'Network segmentation gap analysis and lateral movement path review' },
          { text: 'Compliance-ready outputs for PCI DSS, ISO 27001, NIST, and SOC 2' }
        ]
      }
    ]
  },
  faq: {
    title: 'Configuration Security Review FAQ',
    faqs: [
      {
        question: 'Do you need live access to our devices or just configuration files?',
        answer: 'We can work with either. Many clients provide exported configuration files for offline review, avoiding any risk to live systems. For more comprehensive assessments we can use read-only access to network management platforms.'
      },
      {
        question: 'Which vendors and platforms do you support?',
        answer: 'We support all major vendors including Cisco (IOS, ASA, FTD), Palo Alto Networks, Fortinet FortiGate, Juniper, Check Point, pfSense, and cloud-native controls including AWS Security Groups, Azure NSGs, and GCP VPC Firewall rules.'
      },
      {
        question: 'How long does a configuration review take?',
        answer: 'A focused firewall rule review typically takes 3–5 business days. A full network device configuration review is typically 1–3 weeks depending on device count.'
      },
      {
        question: 'Can this review support our PCI DSS or ISO 27001 audit?',
        answer: 'Yes. Our reports provide audit-ready evidence for PCI DSS Requirement 1, ISO 27001 Annex A.13, and similar controls within NIST and SOC 2 frameworks.'
      }
    ]
  }
};

// COMPLIANCE & SECURITY FRAMEWORK ASSESSMENTS
const complianceFrameworkAssessmentService: CompleteServiceData = {
  hero: {
    icon: '/assets/services/Security.png',
    category: 'Compliance & Governance',
    title: 'Compliance & Security Framework Assessments',
    subtitle: 'Achieve Audit Readiness Without the Guesswork',
    description:
      'Expert-led gap analysis and readiness assessments against ISO 27001 and GDPR — turning compliance from a checkbox exercise into a real security improvement programme.',
    highlights: [
      'ISO 27001:2022 Gap Analysis',
      'GDPR Technical Readiness',
      'Audit-Ready Evidence Packages',
      'Remediation Roadmap Included'
    ],
    ctaButtons: {
      primary: { text: 'Request a Compliance Assessment', link: '/contact' },
      secondary: { text: 'Learn More', link: '/case-studies' }
    },
    leftImage: '/assets/services/kubernetes-left-image.png',
    rightImage: '/static/media/img5.48d8cc8c33ba78943ace.png'
  },
  content: {
    sections: [
      // ── Section 1: Service Offerings ──
      {
        title: 'Compliance Assessment Services',
        type: 'info-cards',
        info_cards_content: [
          {
            id: 'card1',
            number: '01',
            title: 'ISO 27001 Security Compliance Assessment',
            description:
              'Comprehensive gap analysis against ISO 27001:2022 — covering all 93 Annex A controls across information security policies, asset management, access control, cryptography, physical security, supplier relationships, and incident management.',
            features: [
              'ISO 27001:2022 Annex A control gap analysis',
              'ISMS documentation & scope review',
              'Risk assessment framework evaluation',
              'Statement of Applicability (SoA) development support',
              'Audit-ready evidence package preparation',
              'Certification readiness roadmap'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Documentation.png'
          },
          {
            id: 'card2',
            number: '02',
            title: 'GDPR Security & Data Protection Assessment',
            description:
              'Technical and organisational measures assessment against GDPR requirements — covering data mapping, privacy-by-design, consent management, breach notification readiness, data subject rights, and cross-border transfer controls.',
            features: [
              'Data processing activity inventory & mapping (ROPA)',
              'Privacy-by-design & default review',
              'Data subject rights fulfilment assessment',
              'Breach detection & 72-hour notification readiness',
              'Third-party processor due diligence review',
              'Cross-border transfer mechanism validation (SCCs, BCRs)'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Internal-Processes.png'
          }
        ]
      },

      // ── Section 2: Key Areas We Assess ──
      {
        title: 'Key Areas We Assess',
        type: 'animated-info-cards',
        animated_info_cards_content: [
          {
            id: 'card1',
            number: '01',
            title: 'Information Security Policies & Governance',
            description:
              'Review of ISMS documentation, security policies, management commitment, roles and responsibilities, and internal audit processes against ISO 27001 requirements.',
            features: [
              'ISMS scope & boundary review',
              'Policy documentation completeness',
              'Management review evidence assessment'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Pull-Request-Driven.png'
          },
          {
            id: 'card2',
            number: '02',
            title: 'Access Control & Identity Management',
            description:
              'Assessment of user access provisioning, privilege management, MFA enforcement, privileged access reviews, and segregation of duties controls.',
            features: [
              'Privileged access review processes',
              'MFA & authentication control assessment',
              'Segregation of duties analysis'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Access-Control.png'
          },
          {
            id: 'card3',
            number: '03',
            title: 'Data Classification & Asset Management',
            description:
              'Review of data classification schemes, asset inventory completeness, data handling procedures, and retention and disposal policies aligned to ISO 27001 and GDPR.',
            features: [
              'Asset inventory completeness review',
              'Data classification scheme assessment',
              'Retention & disposal policy review'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Managed-Monitoring.png'
          },
          {
            id: 'card4',
            number: '04',
            title: 'Incident Management & Business Continuity',
            description:
              'Evaluation of incident detection, response, and reporting procedures — including GDPR 72-hour breach notification readiness and business continuity plan testing.',
            features: [
              '72-hour breach notification readiness',
              'IR plan completeness & testing evidence',
              'BCP & DR documentation review'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Disaster-Recovery.png'
          },
          {
            id: 'card5',
            number: '05',
            title: 'Supplier & Third-Party Risk Management',
            description:
              'Review of third-party security due diligence processes, Data Processing Agreements (DPAs), supplier security assessments, and ongoing monitoring procedures.',
            features: [
              'DPA & contractual control review',
              'Supplier security assessment process',
              'Third-party access control review'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Cloud-Agnostic.png'
          }
        ]
      },

      // ── Section 3: Why Partner With Us ──
      {
        title: 'Why Partner with Us for Compliance Assessments?',
        background: 'blue',
        type: 'benefits-card',
        benefits_card_content: [
          {
            image: 'Migration&Modernization.png',
            title: 'Practical, Not Theoretical',
            description: 'We translate compliance requirements into concrete, prioritised actions your engineering and security teams can actually implement.',
            features: [
              'Engineering-friendly remediation steps',
              'Prioritised by risk and effort',
              'Actionable control implementation guides'
            ]
          },
          {
            image: 'CloudGovernance.png',
            title: 'Audit-Ready Evidence Packages',
            description: 'We help you assemble the exact evidence packages required by ISO 27001 certification bodies and GDPR supervisory authorities.',
            features: [
              'ISO 27001 certification evidence packs',
              'GDPR Article 30 ROPA templates',
              'DPA & processor agreement templates'
            ]
          },
          {
            image: 'Security&Compliance.png',
            title: 'Deep Technical + Legal Alignment',
            description: 'Our team bridges the gap between technical security controls and regulatory requirements — ensuring compliance is backed by real security.',
            features: [
              'Technical control to regulation mapping',
              'Policy and technical gap correlation',
              'Cross-functional alignment support'
            ]
          },
          {
            image: 'MigrationCostAssessment.png',
            title: 'Continuous Compliance Support',
            description: 'Beyond the initial assessment, we support ongoing compliance maintenance — periodic reviews, internal audit support, and control monitoring.',
            features: [
              'Periodic gap reassessment',
              'Internal audit preparation support',
              'Control monitoring recommendations'
            ]
          }
        ]
      },

      // ── Section 4: Engagement Roadmap ──
      {
        title: 'Compliance Assessment Engagement Roadmap',
        background: 'white',
        type: 'process',
        processSteps: [
          {
            step: 1,
            title: 'Scoping & Context Setting',
            description: 'Define the ISMS scope (ISO 27001) or data processing scope (GDPR). Understand your organisation\'s context, interested parties, and applicable legal and regulatory requirements.'
          },
          {
            step: 2,
            title: 'Documentation Review',
            description: 'Review all existing ISMS documentation, security policies, procedures, risk assessments, SoA, DPIAs, RoPAs, and evidence of control implementation.'
          },
          {
            step: 3,
            title: 'Interviews & Technical Validation',
            description: 'Conduct structured interviews with key stakeholders and perform technical validation of security controls — access control, encryption, logging, and incident response testing.'
          },
          {
            step: 4,
            title: 'Gap Analysis & Risk Scoring',
            description: 'Map findings to ISO 27001:2022 Annex A controls or GDPR articles, score each gap by risk and compliance impact, and identify root cause patterns.'
          },
          {
            step: 5,
            title: 'Reporting & Remediation Roadmap',
            description: 'Deliver a comprehensive gap analysis report with per-control findings, compliance status dashboard, risk-prioritised remediation roadmap, and evidence preparation guidance.'
          },
          {
            step: 6,
            title: 'Remediation Support & Re-Assessment',
            description: 'Provide ongoing support throughout the remediation programme and conduct a follow-up assessment to verify control implementation ahead of external audit or certification.'
          }
        ]
      },

      // ── Section 5: Frameworks Covered ──
      {
        title: 'Compliance Frameworks We Cover',
        background: 'black',
        type: 'list',
        items: [
          'ISO 27001:2022 — Information Security Management System certification readiness',
          'ISO 27002:2022 — Information security controls implementation guidance',
          'GDPR (EU) 2016/679 — General Data Protection Regulation technical compliance',
          'UK GDPR — Post-Brexit UK data protection compliance',
          'ISO 27701 — Privacy Information Management System (PIMS) extension',
          'NIST Privacy Framework — US privacy risk management alignment',
          'ePrivacy Directive — Cookie consent and electronic communications compliance',
          'NIS2 Directive — EU network and information systems security requirements'
        ]
      },

      // ── Section 6: Stats ──
      {
        title: 'Compliance Programme Outcomes',
        type: 'rich-text',
        rich_text_content: [
          { percent: 90, barColor: '#10B981', topic: 'Of Clients Achieve ISO 27001 Certification Within 12 Months' },
          { percent: 75, barColor: '#3B82F6', topic: 'Reduction in Compliance Gaps After First Remediation Cycle' },
          { percent: 85, barColor: '#8B5CF6', topic: 'Of GDPR Breach Notification Processes Improved After Assessment' }
        ]
      },

      // ── Section 7: What You Get ──
      {
        title: 'What You Get',
        background: 'blue',
        type: 'features',
        features_content: [
          { text: 'ISO 27001:2022 Annex A control gap analysis across all 93 controls' },
          { text: 'GDPR technical & organisational measures assessment' },
          { text: 'Risk-prioritised remediation roadmap with effort estimates' },
          { text: 'Statement of Applicability (SoA) development support' },
          { text: 'Audit-ready evidence packages for certification bodies' },
          { text: 'Data Processing Activity Register (ROPA) review and templates' }
        ]
      }
    ]
  },
  faq: {
    title: 'Compliance & Security Framework Assessment FAQ',
    faqs: [
      {
        question: 'What is the difference between an ISO 27001 gap analysis and a formal audit?',
        answer: 'A gap analysis is an internal readiness assessment we conduct to identify what controls are missing before you engage a certification body. A formal audit is conducted by an accredited CB and results in ISO 27001 certification. Our gap analysis prepares you for that formal audit.'
      },
      {
        question: 'How long does an ISO 27001 readiness assessment take?',
        answer: 'For a typical SME (up to 500 employees), an ISO 27001 gap analysis takes 2–4 weeks. For larger enterprises, allow 4–8 weeks depending on existing documentation maturity.'
      },
      {
        question: 'Do you help with GDPR Data Protection Impact Assessments (DPIAs)?',
        answer: 'Yes. As part of our GDPR assessment we review existing DPIAs, identify processing activities that require a DPIA, and provide templates and guidance for conducting new DPIAs that meet Article 35 requirements.'
      },
      {
        question: 'Can you help us prepare for both ISO 27001 and GDPR simultaneously?',
        answer: 'Absolutely. ISO 27001 and GDPR share significant overlap in access control, incident management, supplier management, and data retention. We run both assessments concurrently, identifying controls that satisfy both frameworks simultaneously.'
      },
      {
        question: 'What deliverables do we receive?',
        answer: 'You receive a comprehensive gap analysis report, compliance status dashboard (RAG rating per control), risk-prioritised remediation roadmap, audit-ready evidence preparation guidance, SoA template for ISO 27001, and a ROPA review and DPA template pack for GDPR.'
      }
    ]
  }
};

// Export all services
// AI & GenAI Integration Services
const aiGenAiIntegrationService: CompleteServiceData = {
  hero: {
    icon: '/assets/ai.svg',
    category: 'AI',
    title: 'AI & GenAI Integration Services',
    subtitle: 'Production-Ready AI, Engineered on Solid Infrastructure',
    description: 'Bring LLMs and generative AI into your products with the same rigor we apply to cloud, DevOps, and security — secure, observable, and cost-controlled from day one.',
    highlights: [
      'LLM & RAG Applications',
      'Secure & Compliant by Design',
      'Cost-Controlled Inference',
      'Vendor-Agnostic Integration'
    ],
    ctaButtons: {
      primary: {
        text: 'Book an AI Readiness Call',
        link: '/contact'
      },
      secondary: {
        text: 'Learn More',
        link: '/case-studies'
      }
    },
    leftImage: '/assets/services/ci-cd-left-image.png',
    rightImage: '/assets/services/ci-cd-right-image.png'
  },
  content: {
    sections: [
      {
        title: 'Core AI Integration Offerings',
        type: 'info-cards',
        info_cards_content: [
          {
            id: 'card1',
            number: '01',
            title: 'LLM Application Engineering',
            description: 'Design and build production LLM applications — retrieval-augmented generation, AI assistants, and agent workflows — integrated with your data and existing systems.',
            features: [
              'RAG pipelines with vector databases',
              'Agent workflows and tool integration',
              'Claude, GPT, and open-source model support'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Flow.png'
          },
          {
            id: 'card2',
            number: '02',
            title: 'GenAI Platform & Gateway',
            description: 'Stand up a governed AI gateway that gives every team safe access to models — with routing, caching, rate limits, and full usage attribution.',
            features: [
              'Centralized model routing and fallbacks',
              'Prompt and response logging',
              'Per-team usage and cost attribution'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Multi-Cloud.png'
          },
          {
            id: 'card3',
            number: '03',
            title: 'AI Security & Guardrails',
            description: 'Protect AI features against prompt injection, data leakage, and unsafe outputs with layered guardrails and continuous adversarial testing.',
            features: [
              'Prompt-injection and jailbreak defense',
              'PII redaction and data-loss prevention',
              'AI-specific penetration testing'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Access-Control.png'
          },
          {
            id: 'card4',
            number: '04',
            title: 'LLMOps & Evaluation',
            description: 'Ship AI changes with confidence using automated evaluation suites, regression testing for prompts, and production observability for quality and cost.',
            features: [
              'Automated eval pipelines in CI/CD',
              'Token cost monitoring and budgets',
              'Latency and quality dashboards'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Observability.png'
          }
        ]
      },
      {
        title: 'Our AI Integration Process',
        type: 'process',
        background: 'white',
        processSteps: [
          {
            step: 1,
            title: 'Assess & Prioritize',
            description: 'Identify the AI use cases with real ROI, evaluate data readiness, and define success metrics and guardrail requirements up front.'
          },
          {
            step: 2,
            title: 'Prototype & Validate',
            description: 'Build a working pilot in weeks, evaluated against your quality benchmarks — not a demo that falls apart in production.'
          },
          {
            step: 3,
            title: 'Productionize',
            description: 'Harden the pilot with security controls, observability, cost limits, and CI/CD — deployed on infrastructure you own.'
          },
          {
            step: 4,
            title: 'Operate & Improve',
            description: 'Monitor quality, cost, and drift in production, with evaluation-driven iteration and full knowledge transfer to your team.'
          }
        ]
      },
      {
        title: 'What You Get',
        background: 'blue',
        type: 'features',
        features_content: [
          {
            text: 'Production LLM applications integrated with your data and workflows'
          },
          {
            text: 'A governed AI gateway with usage, cost, and access controls'
          },
          {
            text: 'Guardrails against prompt injection, data leakage, and unsafe outputs'
          },
          {
            text: 'Automated evaluation and regression testing in CI/CD'
          },
          {
            text: 'Full observability of AI quality, latency, and spend'
          },
          {
            text: 'Compliance-aware design aligned with SOC 2, GDPR, and ISO 27001'
          }
        ]
      }
    ]
  },
  faq: {
    title: 'AI & GenAI Integration FAQ',
    faqs: [
      {
        question: 'Which AI models and providers do you work with?',
        answer: 'We are vendor-agnostic. We integrate Anthropic Claude, OpenAI GPT, Google Gemini, and open-source models like Llama and Mistral — often behind a gateway so you can switch or mix providers without rewriting your application.'
      },
      {
        question: 'How do you keep our data safe when using LLMs?',
        answer: 'We design for data protection from the start: private or VPC-hosted model endpoints where required, PII redaction before prompts leave your boundary, strict retention policies, and audit logging aligned with SOC 2, GDPR, and ISO 27001 requirements.'
      },
      {
        question: 'How do you control AI inference costs?',
        answer: 'We implement token budgets, semantic caching, model routing that sends simple requests to cheaper models, and per-team cost attribution — the same FinOps discipline we apply to cloud infrastructure, applied to AI spend.'
      },
      {
        question: 'How long does a typical AI integration project take?',
        answer: 'A validated pilot typically takes 2–4 weeks, with production hardening in another 4–6 weeks depending on security and compliance requirements. We define evaluation criteria first, so you know the pilot works before investing in scale.'
      }
    ]
  }
};

// MLOps & ML Platform Engineering
const mlopsPlatformService: CompleteServiceData = {
  hero: {
    icon: '/assets/ml.svg',
    category: 'MLOps',
    title: 'MLOps & ML Platform Engineering',
    subtitle: 'From Notebook to Production — Reliably and at Scale',
    description: 'Build the platform your ML teams need: automated pipelines, scalable GPU infrastructure, and monitored deployments that treat models like production software.',
    highlights: [
      'Automated ML Pipelines',
      'GPU Infrastructure on Kubernetes',
      'Model Serving & Monitoring',
      'CI/CD for Models'
    ],
    ctaButtons: {
      primary: {
        text: 'Book an MLOps Assessment',
        link: '/contact'
      },
      secondary: {
        text: 'Learn More',
        link: '/case-studies'
      }
    },
    leftImage: '/assets/services/kubernetes-left-image.png',
    rightImage: '/assets/services/kubernetes-right-image.png'
  },
  content: {
    sections: [
      {
        title: 'Core MLOps Offerings',
        type: 'info-cards',
        info_cards_content: [
          {
            id: 'card1',
            number: '01',
            title: 'ML Pipeline Automation',
            description: 'Replace manual, notebook-driven workflows with versioned, reproducible pipelines covering data preparation, training, validation, and deployment.',
            features: [
              'Kubeflow, MLflow, and Airflow pipelines',
              'Experiment tracking and model registry',
              'Reproducible, version-controlled training'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Automation-First.png'
          },
          {
            id: 'card2',
            number: '02',
            title: 'Model Serving & Inference Infrastructure',
            description: 'Serve models with production-grade latency, autoscaling, and rollout safety — from real-time APIs to batch scoring and LLM inference.',
            features: [
              'KServe, Triton, and vLLM deployments',
              'Autoscaling with scale-to-zero',
              'Canary and shadow deployments for models'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Kubernetes-Platform.png'
          },
          {
            id: 'card3',
            number: '03',
            title: 'GPU Infrastructure & Cost Optimization',
            description: 'Design Kubernetes-based GPU platforms that maximize utilization and minimize spend — the FinOps discipline your AI workloads need.',
            features: [
              'GPU scheduling, sharing, and quotas',
              'Spot and reserved capacity strategies',
              'Per-team GPU cost attribution'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Kubernetes-Cost.png'
          },
          {
            id: 'card4',
            number: '04',
            title: 'Model Monitoring & Drift Detection',
            description: 'Know when models degrade before your users do — with monitoring for data drift, prediction quality, and infrastructure health in one place.',
            features: [
              'Data and concept drift detection',
              'Prometheus and Grafana integration',
              'Automated retraining triggers'
            ],
            bgColor: 'white',
            image: '/assets/allServices/Managed-Monitoring.png'
          }
        ]
      },
      {
        title: 'Our MLOps Implementation Process',
        type: 'process',
        background: 'white',
        processSteps: [
          {
            step: 1,
            title: 'Platform Assessment',
            description: 'Map your current ML workflow from data to deployment, identify bottlenecks and risks, and define the target platform architecture.'
          },
          {
            step: 2,
            title: 'Foundation Build',
            description: 'Stand up the core platform — pipelines, registry, serving, and GPU infrastructure — as Infrastructure-as-Code your team owns.'
          },
          {
            step: 3,
            title: 'Migration & Automation',
            description: 'Move existing models onto the platform with CI/CD, automated testing, and progressive delivery for every model release.'
          },
          {
            step: 4,
            title: 'Enablement & Handover',
            description: 'Train your data scientists and engineers on the platform, with runbooks, documentation, and monitoring in place from day one.'
          }
        ]
      },
      {
        title: 'What You Get',
        background: 'blue',
        type: 'features',
        features_content: [
          {
            text: 'End-to-end ML pipelines from data preparation to deployment'
          },
          {
            text: 'Production model serving with autoscaling and safe rollouts'
          },
          {
            text: 'Cost-optimized GPU infrastructure on Kubernetes'
          },
          {
            text: 'Model and data drift monitoring with automated alerts'
          },
          {
            text: 'CI/CD and versioning for models, data, and features'
          },
          {
            text: 'A platform your team owns — no vendor lock-in'
          }
        ]
      }
    ]
  },
  faq: {
    title: 'MLOps & ML Platform FAQ',
    faqs: [
      {
        question: 'Which MLOps tools and platforms do you work with?',
        answer: 'We build on open standards: Kubeflow, MLflow, Airflow, KServe, Triton, vLLM, and Ray, plus managed services like SageMaker and Vertex AI where they fit. We recommend based on your team, workloads, and cloud footprint — not a fixed stack.'
      },
      {
        question: 'Can you reduce our GPU costs?',
        answer: 'Yes. GPU utilization below 30% is common before optimization. Through scheduling, right-sizing, spot capacity, and workload consolidation, we typically cut GPU spend significantly while improving training and inference throughput.'
      },
      {
        question: 'Do you support LLM workloads as well as classical ML?',
        answer: 'Absolutely. We build serving infrastructure for both — from scikit-learn and XGBoost models to fine-tuned LLMs served with vLLM on autoscaling GPU clusters, with the same pipeline, monitoring, and cost controls.'
      },
      {
        question: 'How long does it take to stand up an ML platform?',
        answer: 'A production-ready foundation — pipelines, registry, serving, and monitoring — typically takes 4–8 weeks. We migrate your highest-value model first so the platform proves itself early.'
      }
    ]
  }
};

export const EnhancedAllServicesData: { [key: string]: CompleteServiceData } = {
  'cloud-cost-optimization': cloudCostOptimizationService,
  'kubernetes-consulting': kubernetesConsultingService,
  'terraform-pulumi': terraformPulumiService,
  'sre-consulting': sreConsultingService,
  'prometheus-grafana': prometheusGrafanaService,
  'policy-as-code': policyAsCodeService,
  'gitops-consulting': gitopsService,
  'infrastructure-design': infrastructureDesignService,
  'cloud-migration': cloudMigrationService,
  'cloud-compliance': complianceSecurityService,
  'ci-cd-services': cicdService,
  'backup-disaster-recovery': backupDrService,
  'penetration-testing': penetrationTestingService,
  'red-team-adversary-simulation': redTeamService,
  'configuration-security-review': configSecurityReviewService,
  'compliance-framework-assessment': complianceFrameworkAssessmentService,
  'ai-genai-integration': aiGenAiIntegrationService,
  'mlops-platform-engineering': mlopsPlatformService
};
  